import React, { useState, useRef, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

const API = window.location.origin + '/api';

// ─────────────────────────────────────────────
//  DESIGN TOKENS
// ─────────────────────────────────────────────
const T = {
  bg:      "#0f1410",
  surface: "#161b15",
  card:    "#1c2219",
  card2:   "#1f2620",
  border:  "#2a3328",
  accent:  "#6d9b3e",
  tan:     "#c4b080",
  light:   "#d8d4c8",
  dim:     "#8a9082",
  faint:   "#4a5048",
  gold:    "#b09030",
  red:     "#8e3428",
  blue:    "#2e6480",
  olive:   "#2c3828",
  green:   "#2a4a20",
};
const sans = "'Segoe UI', system-ui, sans-serif";
const mono = "'Courier New', monospace";
const STATUS_C = { PENDING:T.gold, APPROVED:T.accent, DENIED:T.red, "IN REVIEW":T.blue };

// ─────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────
const RANKS = ["RCT","PVT","CPL","SGT","SSG","SFC","MSG","1SG","2LT","1LT","CPT","MAJ","LTC","COL"];
const AWARDS = [
  "Life Saving","Leadership Excellence","Meritorious Service","Commendation","Achievement",
  "Cadet of the Year","Regimental Commander's List","Rook/Cadet of the Month","Sports Captain",
  "Academic Excellence","Good Conduct","Humanitarian Service","Voluntary Service","Community Service",
  "Kreitzberg Challenge","Fitness Excellence","Leader Training","Second Year Training","Junior Ring",
  "Initial Entry Training","Recruiting","Foreign Service","Legacy March","Semper Fi Society","Semper Paratus Society",
];
const COMPANIES = {
  "ALPHA CO":   { building:"Goodyear",  floors:4, roomsPerFloor:20 },
  "BRAVO CO":   { building:"Patterson", floors:4, roomsPerFloor:20 },
  "CHARLIE CO": { building:"Dodge",     floors:4, roomsPerFloor:20 },
  "DELTA CO":   { building:"Hawkins",   floors:4, roomsPerFloor:20 },
  "BATTALION STAFF": { building:"Dodge", floors:0, roomsPerFloor:0 },
};
const CO_LIST = Object.keys(COMPANIES);
const FORMATION_TYPES = ["Morning Formation","Marchdown","Pass & Review","Other"];

// Accountability absence types — the master-list status board tracks all four
// as distinct columns (P is implicit: present = no absence record for that day).
const ABSENCE_TYPES = [
  { value:"UNEXCUSED", label:"Unexcused" },
  { value:"EXCUSED",   label:"Excused"   },
  { value:"LEAVE",     label:"Leave"     },
  { value:"SAB",       label:"Study Abroad" },
];
// Absence-type value → the 2-3 letter code shown on the status board (matches
// battalion accountability-tracker convention: P / EA / UA / LV / SAB).
const STATUS_CODE = { UNEXCUSED:"UA", EXCUSED:"EA", LEAVE:"LV", SAB:"SAB" };
const STATUS_CODE_LABEL = { P:"Present", EA:"Excused Absent", UA:"Unexcused Absent", LV:"Leave", SAB:"Study Abroad", NR:"No Report" };
// Vivid fill colors for the status-count legend/summary table, taken directly
// from the battalion accountability-tracker template.
const STATUS_BOARD_FILL = { P:"FF00B050", EA:"FFFFC000", UA:"FF7030A0", LV:"FFFFFF00", SAB:"FF0070C0", NR:"FFD9D9D9" };

// Duty title (as stored on the roster) → short position code shown in each
// cadet's box on the status board, matching the battalion accountability
// tracker's convention. Unmapped duties fall back to the raw duty text.
const POSITION_MAP = {
  "COMPANY COMMANDER":"CO", "COMPANY XO":"XO", "COMPANY 1SG":"1SG",
  "PLATOON LEADER":"PL", "PLATOON SERGEANT":"PSG",
  "SQUAD LEADER":"SQD LDR", "ASSISTANT SQUAD LEADER":"ASL", "SQUAD MEMBER":"PVT", "RECRUIT":"PVT",
  "TRAINING OFFICER":"TR OIC", "TROOP 1SG":"TR NCOIC", "TRAINING CLERK":"TR CPL",
  "ADMIN CLERK":"CLERK", "STAFF CLERK":"CLERK", "KNOWLEDGE MANAGEMENT CLERK":"CLERK", "FITNESS CLERK":"CLERK",
  "ADMIN NCO":"ADMIN", "ATHLETIC LIAISON":"ALO", "CAREER DEVELOPMENT OFFICER":"CDO",
  "BATTALION COMMANDER":"BC", "BATTALION XO":"BN XO", "BATTALION CSM":"CSM",
  "BATTALION COLOR BEARER/RUNNER":"COLORS", "BATTALION RUNNER":"RUNNER",
  "BATTALION S1 OFFICER":"OIC", "BATTALION S2 OFFICER":"OIC", "BATTALION S3 OFFICER":"OIC",
  "BATTALION S4 OFFICER":"OIC", "BATTALION S5 OFFICER":"OIC", "BATTALION PA OFFICER":"OIC",
  "BATTALION S2 STAFF CLERK":"CLERK", "BATTALION PA ASSISTANT":"CLERK",
  "BATTALION TRAINING NCO":"NCO", "BATTALION MFT":"MFT OIC", "BATTALION MFT NCO":"MFT NCO",
};
function positionCode(duty) {
  const key = (duty || "").trim().toUpperCase();
  if (!key) return "";
  return POSITION_MAP[key] || duty.trim();
}
// Relative display order for position codes within a platoon/squad/HQ block —
// leadership first, then general members. Unlisted codes sort after these.
const POSITION_ORDER = ["CO","XO","1SG","BC","BN XO","CSM","PL","PSG","OIC","NCO","SQD LDR","ASL","TR OIC","TR NCOIC","TR CPL","MFT OIC","MFT NCO","CLERK","ADMIN","COLORS","RUNNER","ALO","CDO","PVT"];

// Resolve a cadet's status for one formation report → { code, reason }
// code is one of P / EA / UA / LV / SAB / NR (no report filed for that date).
function getCadetDayStatus(cadet, dayReport) {
  if (!dayReport) return { code:"NR", reason:"" };
  if (dayReport.details?.allAccounted) return { code:"P", reason:"" };
  const absence = (dayReport.details?.absences || []).find(a =>
    (a.name || "").toUpperCase().includes((cadet.last || "").toUpperCase())
  );
  if (!absence) return { code:"P", reason:"" };
  return { code: STATUS_CODE[absence.status] || "UA", reason: absence.reason || "" };
}


// ─────────────────────────────────────────────
//  ROSTER PARSING HELPERS
// ─────────────────────────────────────────────

// Normalize company name from various forms → canonical key
// Handles: "A","B","C","D" single letters; "ALPHA","BRAVO","CHARLIE","DELTA";
//           "ALPHA CO","BRAVO CO","A CO"; "ACO","BCO","CCO","DCO" (no space);
//           "ALPHA COMPANY" etc.
function normalizeCompany(raw) {
  if (!raw) return "";
  const s = raw.toString().trim().toUpperCase().replace(/\s+/g," ");

  // Battalion Staff variations
  if (s.includes("BATTALION") && s.includes("STAFF")) return "BATTALION STAFF";
  if (s === "BN STAFF" || s === "BNSTAFF" || s === "BN") return "BATTALION STAFF";
  if (s === "HQ" || s === "HEADQUARTERS") return "BATTALION STAFF";

  // Normalize wordy variants, then strip a trailing "CO" (with or without a
  // space/preceding letter run-on — covers "ALPHA CO", "A CO", and "ACO" alike)
  // down to the bare letter/word before matching.
  const norm = s
    .replace(/\bCOMPANY\b/g,"CO").replace(/\bCOMP\b/g,"CO")
    .replace(/\s*CO$/,"");

  const letterMap = { A:"ALPHA CO", B:"BRAVO CO", C:"CHARLIE CO", D:"DELTA CO",
    ALPHA:"ALPHA CO", BRAVO:"BRAVO CO", CHARLIE:"CHARLIE CO", DELTA:"DELTA CO" };
  if (letterMap[norm]) return letterMap[norm];

  for (const key of CO_LIST) {
    if (s === key || norm === key) return key;
    const short = key.replace(" CO","");
    if (norm === short) return key;
    if (s.includes(key) || norm.includes(key)) return key;
  }
  return "";
}

// Roster columns — official format: LastName FirstName MIName BN UNIT PLT RANK DUTY CADET_YEAR Email_Address
// SQD and ROOM are optional trailing columns (11/12) — added so a bulk roster
// upload can populate the accountability status-board's squad/room grouping
// in one pass; omitting them is still valid and leaves those fields blank.
const ROSTER_COLS = ["last","first","mi","bn","unit","plt","rank","duty","cadetYear","email","sqd","room"];
const ROSTER_HEADER_LABELS = ["LastName","FirstName","MIName","BN","UNIT","PLT","RANK","DUTY","CADET_YEAR","Email_Address","SQD","ROOM"];
const ROSTER_HEADER_WIDTHS = [18,14,8.43,8,8.43,8.43,20,8.43,12,26,6,8];

// Parse a single tab/space-delimited text line in column order
function parseLine(line) {
  const clean = line.trim();
  if (!clean) return null;
  // Support tab-separated (copy from spreadsheet) or multi-space
  const parts = clean.split(/\t/).length > 1
    ? clean.split(/\t/).map(p=>p.trim())
    : clean.split(/\s{2,}/).map(p=>p.trim());
  if (parts.length < 3) return null;
  // Map by position: LastName FirstName MIName BN UNIT PLT RANK DUTY CADET_YEAR Email_Address [SQD] [ROOM]
  const [last="", first="", mi="", bn="", unitRaw="", plt="", rank="", duty="", cadetYear="", email="", sqd="", room=""] = parts;
  const unit = normalizeCompany(unitRaw);
  if (!last) return null;
  return {
    last: last.toUpperCase(), first: first.toUpperCase(), mi: mi.toUpperCase(),
    bn: bn.toUpperCase(), unit, plt: plt.toUpperCase(),
    rank: rank.toUpperCase(), duty: duty.trim(),
    cadetYear: cadetYear.toUpperCase(), email: email.trim().toLowerCase(),
    sqd: sqd.toUpperCase(), room: room.toUpperCase(),
  };
}

// Parse pasted text block → array of cadets
function parseTextBlock(text) {
  const lines = text.split(/\n/).filter(l => l.trim());
  const cadets = [];
  const errors = [];
  // Skip header line if first line looks like headers
  const headerTest = lines[0]?.toLowerCase() || "";
  const startIdx = (headerTest.includes("lastname") || headerTest.includes("last") || headerTest.includes("email")) ? 1 : 0;
  lines.slice(startIdx).forEach((line, i) => {
    const parsed = parseLine(line);
    if (parsed) cadets.push(parsed);
    else errors.push({ line: startIdx+i+1, text: line });
  });
  return { cadets, errors };
}

// Parse uploaded Excel/CSV — expects columns in order:
// LastName FirstName MIName BN UNIT PLT RANK DUTY CADET_YEAR Email_Address
function parseSpreadsheet(file, callback) {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(e.target.result, { type:"binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header:1, defval:"" });
      const cadets = [];
      const errors = [];
      // Auto-detect header row
      const headerKws = ["lastname","firstname","miname","cadet_year","email_address"];
      let startRow = 0;
      if (rows[0]) {
        const h = rows[0].map(c=>c.toString().toLowerCase().replace(/\s+/g,""));
        if (headerKws.some(k=>h.some(hh=>hh.includes(k)))) startRow = 1;
      }
      rows.slice(startRow).forEach((row, i) => {
        if (!row.some(c=>c)) return;
        // Columns by position: 0=LastName 1=FirstName 2=MIName 3=BN 4=UNIT 5=PLT 6=RANK 7=DUTY 8=CADET_YEAR 9=Email_Address
        const get = (idx) => (row[idx]||"").toString().trim();
        const last = get(0).toUpperCase();
        if (!last) { errors.push({ line:startRow+i+1, text:row.join(" | ") }); return; }
        cadets.push({
          last,
          first:     get(1).toUpperCase(),
          mi:        get(2).toUpperCase(),
          bn:        get(3).toUpperCase(),
          unit:      normalizeCompany(get(4)),
          plt:       get(5).toUpperCase(),
          rank:      get(6).toUpperCase(),
          duty:      get(7),
          cadetYear: get(8).toUpperCase(),
          email:     get(9).toLowerCase(),
          sqd:       get(10).toUpperCase(),
          room:      get(11).toUpperCase(),
        });
      });
      callback({ cadets, errors });
    } catch(err) {
      callback({ cadets:[], errors:[{ line:0, text:"File could not be parsed: "+err.message }] });
    }
  };
  reader.readAsBinaryString(file);
}

// ─────────────────────────────────────────────
//  EXCEL EXPORTS — per section
// ─────────────────────────────────────────────
// Title-cases a company key, e.g. "ALPHA CO" → "Alpha", "BATTALION STAFF" → "Battalion Staff"
function titleCase(s) {
  return s.split(" ").filter(Boolean).map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(" ");
}
const companyShortTitle = (co) => titleCase(co.replace(" CO", ""));
const rosterSheetName    = (co) => `${companyShortTitle(co)} Roster`;

// Official roster row: LastName FirstName MIName BN UNIT PLT RANK DUTY CADET_YEAR Email_Address
function rosterExportRow(r) {
  return {
    LastName: r.last || "", FirstName: r.first || "", MIName: r.mi || "",
    BN: r.bn || "", UNIT: r.unit || "", PLT: r.plt || "", RANK: r.rank || "",
    DUTY: r.duty || "", CADET_YEAR: r.cadetYear || "", Email_Address: r.email || "",
    SQD: r.sqd || "", ROOM: r.room || "",
  };
}

function matchRosterCadet(s, roster) {
  return roster.find(r => r.last && s.cadet && s.cadet.toUpperCase().includes(r.last.toUpperCase()));
}

// Submission row builder — roster columns first (official format), then submission-specific fields
function submissionRosterRow(s, roster, extra) {
  const rMatch = matchRosterCadet(s, roster);
  const base = rosterExportRow({
    last: rMatch?.last || (s.cadet?.split(",")[0]?.split(" ").slice(1).join(" ") || s.cadet || ""),
    first: rMatch?.first || (s.cadet?.split(",")[1]?.trim() || ""),
    mi: rMatch?.mi, bn: rMatch?.bn, unit: s.unit || rMatch?.unit,
    plt: rMatch?.plt, rank: rMatch?.rank || (s.cadet?.split(" ")[0] || ""),
    duty: rMatch?.duty, cadetYear: rMatch?.cadetYear, email: rMatch?.email,
  });
  return {
    ...base,
    "Ref ID": s.id, "Date Submitted": s.date, "Priority": s.priority, "S1 Note": s.s1Note || "",
    ...(extra || {}),
  };
}

// Applies the official header style: bold white text on blue fill, centered
function styleRosterHeaderRow(row) {
  row.eachCell(cell => {
    cell.font      = { bold:true, size:11, color:{ argb:"FFFFFFFF" } };
    cell.fill      = { type:"pattern", pattern:"solid", fgColor:{ argb:"FF4472C4" } };
    cell.alignment = { horizontal:"center", vertical:"bottom" };
  });
  row.height = 15;
}

// Thin bottom border on each data row, matching the official format
function styleDataRow(row) {
  row.eachCell(cell => { cell.border = { bottom:{ style:"thin", color:{ argb:"FF000000" } } }; });
  row.height = 15;
}

// Builds one worksheet in the official format: styled header, bordered rows, frozen header row
function addFormattedSheet(wb, name, headers, widths, rows) {
  const ws = wb.addWorksheet(name.slice(0, 31), { views: [{ state:"frozen", ySplit:1 }] });
  ws.columns = headers.map((h, i) => ({ header:h, key:h, width: widths?.[i] || 12 }));
  styleRosterHeaderRow(ws.getRow(1));
  const dataRows = rows.length ? rows : [{ [headers[0]]: "No records" }];
  dataRows.forEach(r => styleDataRow(ws.addRow(headers.map(h => r[h] ?? ""))));
  return ws;
}

async function downloadWorkbook(wb, filename) {
  const buf  = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function exportSection(type, allSubmissions, roster) {
  const wb   = new ExcelJS.Workbook();
  const date = new Date().toISOString().split("T")[0];

  // ── FULL ROSTER ──
  if (type === "Roster") {
    addFormattedSheet(wb, "All Cadets", ROSTER_HEADER_LABELS, ROSTER_HEADER_WIDTHS, roster.map(rosterExportRow));
    CO_LIST.forEach(co => {
      addFormattedSheet(wb, rosterSheetName(co), ROSTER_HEADER_LABELS, ROSTER_HEADER_WIDTHS,
        roster.filter(r => r.unit === co).map(rosterExportRow));
    });
    await downloadWorkbook(wb, `S1_FullRoster_${date}.xlsx`); return;
  }

  // ── INDIVIDUAL COMPANY ROSTER ──
  if (CO_LIST.includes(type)) {
    addFormattedSheet(wb, rosterSheetName(type), ROSTER_HEADER_LABELS, ROSTER_HEADER_WIDTHS,
      roster.filter(r => r.unit === type).map(rosterExportRow));
    await downloadWorkbook(wb, `${companyShortTitle(type)}_Company_Roster_${date}.xlsx`); return;
  }

  // For submission exports: APPROVED ONLY
  const approved = allSubmissions.filter(s => s.status === "APPROVED");
  const subHeaders = (extraHeaders) => [...ROSTER_HEADER_LABELS, "Ref ID", "Date Submitted", "Priority", "S1 Note", ...extraHeaders];
  const subWidths  = (extraWidths)  => [...ROSTER_HEADER_WIDTHS, 10, 14, 10, 30, ...extraWidths];

  // ── ALL SUBMISSIONS ──
  if (type === "All") {
    const rows = approved.map(s => submissionRosterRow(s, roster));
    addFormattedSheet(wb, "All Submissions", subHeaders([]), subWidths([]), rows);
    await downloadWorkbook(wb, `S1_AllSubmissions_${date}.xlsx`); return;
  }

  // Helper: build all + per-company sheets for a given type
  const buildTypeExport = async (typeName, extraFn, extraHeaders, extraWidths, filename) => {
    const typeSubs = approved.filter(s => s.type === typeName);
    const headers = subHeaders(extraHeaders), widths = subWidths(extraWidths);
    addFormattedSheet(wb, typeName, headers, widths, typeSubs.map(s => submissionRosterRow(s, roster, extraFn(s.details || {}))));
    CO_LIST.forEach(co => {
      const coSubs = typeSubs.filter(s => s.unit === co);
      if (!coSubs.length) return;
      addFormattedSheet(wb, rosterSheetName(co), headers, widths, coSubs.map(s => submissionRosterRow(s, roster, extraFn(s.details || {}))));
    });
    await downloadWorkbook(wb, filename);
  };

  if (type === "Promotion") {
    await buildTypeExport("Promotion",
      d => ({ "From Rank":d.promoFrom||"","To Rank":d.promoTo||"","Effective Date":d.promoDate||"","Justification":d.promoJust||"" }),
      ["From Rank","To Rank","Effective Date","Justification"], [10,10,14,40],
      `S1_Promotions_${date}.xlsx`
    ); return;
  }

  if (type === "Award") {
    await buildTypeExport("Award",
      d => ({ "Award":d.awardName||"","Ceremony Date":d.awardDate||"","Narrative":d.awardNarrative||"" }),
      ["Award","Ceremony Date","Narrative"], [28,14,50],
      `S1_Awards_${date}.xlsx`
    ); return;
  }

  if (type === "Room Transfer") {
    await buildTypeExport("Room Transfer",
      d => ({ "From Company":d.fromCo||"","From Room":d.fromRoom||"","To Company":d.toCo||"","To Room":d.toRoom||"","Effective Date":d.transferDate||"","Reason":d.transferReason||"" }),
      ["From Company","From Room","To Company","To Room","Effective Date","Reason"], [14,10,14,10,14,30],
      `S1_RoomTransfers_${date}.xlsx`
    ); return;
  }

  if (type === "Accountability") {
    const acctHeaders = [...subHeaders(["Formation Type","Formation Date/Time","All Accounted","# Unaccounted"])];
    const acctWidths  = [...subWidths([18,20,14,14])];
    const acctExtra = s => ({
      "Formation Type": s.details?.formationType || "",
      "Formation Date/Time": s.details?.formationDate || "",
      "All Accounted": s.details?.allAccounted ? "YES" : "NO",
      "# Unaccounted": (s.details?.absences || []).filter(a => a.status === "UNEXCUSED").length,
    });

    // Summary sheet (all companies) — approved only
    const acctSubs = approved.filter(s => s.type === "Accountability");
    addFormattedSheet(wb, "Summary", acctHeaders, acctWidths,
      acctSubs.map(s => submissionRosterRow(s, roster, acctExtra(s))));

    // Detail sheet — unaccounted cadets, roster format + formation/absence info
    const detailHeaders = [...ROSTER_HEADER_LABELS, "Ref ID", "Formation Type", "Absence Type", "Reason"];
    const detailWidths  = [...ROSTER_HEADER_WIDTHS, 10, 18, 12, 30];
    const detailRows = [];
    acctSubs.forEach(s => {
      (s.details?.absences || []).filter(a => a.status === "UNEXCUSED").forEach(a => {
        const rMatch = roster.find(r => r.last && a.name?.toUpperCase().includes(r.last.toUpperCase()));
        detailRows.push({
          ...rosterExportRow({
            last: rMatch?.last || a.name || "", first: rMatch?.first, mi: rMatch?.mi,
            bn: rMatch?.bn, unit: s.unit, plt: rMatch?.plt, rank: a.rank || rMatch?.rank,
            duty: rMatch?.duty, cadetYear: rMatch?.cadetYear, email: rMatch?.email,
          }),
          "Ref ID": s.id, "Formation Type": s.details?.formationType || "",
          "Absence Type": a.status, "Reason": a.reason || "",
        });
      });
    });
    addFormattedSheet(wb, "Absent Cadets Detail", detailHeaders, detailWidths, detailRows);

    // Per-company sheets
    CO_LIST.forEach(co => {
      const coSubs = acctSubs.filter(s => s.unit === co);
      if (!coSubs.length) return;
      addFormattedSheet(wb, rosterSheetName(co), acctHeaders, acctWidths,
        coSubs.map(s => submissionRosterRow(s, roster, acctExtra(s))));
    });
    await downloadWorkbook(wb, `S1_Accountability_${date}.xlsx`);
  }
}

// ─────────────────────────────────────────────
//  ACCOUNTABILITY STATUS BOARD — one workbook
//  sheet per company per day, matching the
//  battalion accountability-tracker's format:
//  HQ/leadership block, platoon → squad blocks,
//  P/EA/UA/LV/SAB status legend, status-count
//  summary. Shared by the calendar-by-date
//  download, the per-company quick-download,
//  and the S1 combined battalion workbook.
// ─────────────────────────────────────────────

// Full rank text (as imported) → short code shown on the board.
const RANK_MAP = {
  "RECRUIT":"RCT", "PRIVATE":"PVT", "PRIVATE FIRST CLASS":"PVT", "CORPORAL":"CPL",
  "SERGEANT":"SGT", "STAFF SERGEANT":"SSG", "SERGEANT FIRST CLASS":"SFC", "MASTER SERGEANT":"MSG",
  "FIRST SERGEANT":"1SG", "SERGEANT MAJOR":"SGM", "COMMAND SERGEANT MAJOR":"CSM",
  "SECOND LIEUTENANT":"2LT", "FIRST LIEUTENANT":"1LT", "CAPTAIN":"CPT",
  "MAJOR":"MAJ", "LIEUTENANT COLONEL":"LTC", "COLONEL":"COL",
};
function rankCode(rank) {
  const key = (rank || "").trim().toUpperCase();
  if (!key) return "";
  return RANK_MAP[key] || rank.trim();
}

// Groups a company roster into display blocks (HQ first, numbered platoons in
// order, staff sections, then an "unassigned" catch-all for blank platoon —
// never silently drops cadets even when roster data is incomplete).
// Ordinal suffix for platoon numbers, e.g. 1 → "1ST", 2 → "2ND" — matches the
// battalion tracker's "1ST PLATOON" / "2ND PLATOON" convention.
const ORDINAL_SUFFIX = { 1:"ST", 2:"ND", 3:"RD" };
const ordinal = (n) => `${n}${ORDINAL_SUFFIX[n] || "TH"}`;

// Normalize a raw roster platoon value ("1", "1ST", "1ST PLATOON", "FIRST",
// "PLT 1"...) down to a bare canonical key ("1") so cadets with differently
// formatted platoon entries (e.g. leadership rows using "1ST" while line
// cadets use "1") land in one merged group instead of two duplicate blocks.
function normalizePlatoonKey(raw) {
  const s = (raw || "").trim().toUpperCase();
  if (!s || s === "HQ") return s;
  const stripped = s.replace(/\bPLATOON\b/g,"").replace(/\bPLT\b/g,"").trim();
  const WORD_MAP = { FIRST:"1", SECOND:"2", THIRD:"3", FOURTH:"4", FIFTH:"5" };
  if (WORD_MAP[stripped]) return WORD_MAP[stripped];
  const m = stripped.match(/^(\d+)(?:ST|ND|RD|TH)?$/);
  if (m) return m[1];
  return stripped;
}

function groupRosterForBoard(coRoster) {
  const STAFF_ORDER = ["S1","S2","S3","S4","S5","PA","ALO"];
  const byPlt = {};
  coRoster.forEach(c => {
    const key = normalizePlatoonKey(c.plt);
    (byPlt[key] = byPlt[key] || []).push(c);
  });
  const rankOf = (key) => {
    if (key === "HQ") return -1000;
    const staffIdx = STAFF_ORDER.indexOf(key);
    if (staffIdx >= 0) return 500 + staffIdx;
    if (key === "") return 100000;
    const n = parseInt(key, 10);
    return isNaN(n) ? 900 : n;
  };
  const labelOf = (key, companyLabel) => {
    if (key === "HQ") return `${companyLabel} HQ / LEADERSHIP`;
    if (key === "") return "UNASSIGNED — NO PLATOON ON ROSTER";
    if (STAFF_ORDER.includes(key)) return `${key} SECTION`;
    const n = parseInt(key, 10);
    if (!isNaN(n)) return `${ordinal(n)} PLATOON`;
    return key;
  };
  return Object.keys(byPlt)
    .sort((a, b) => rankOf(a) - rankOf(b))
    .map(key => ({ key, cadets: byPlt[key], labelOf }));
}

// Sub-groups a platoon's cadets into squads (blank squad → single ungrouped
// bucket, so the layout degrades gracefully until squad data is imported).
function groupBySquad(cadets) {
  const norm = v => (v || "").trim().toUpperCase();
  const squads = [...new Set(cadets.map(c => norm(c.sqd)))];
  if (squads.length === 1 && squads[0] === "") return [{ label:null, cadets }];
  return squads
    .sort((a, b) => {
      const na = parseInt(a, 10), nb = parseInt(b, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      if (a === "") return 1;
      if (b === "") return -1;
      return a.localeCompare(b);
    })
    .map(sq => ({
      label: sq === "" ? "UNASSIGNED SQUAD" : `${sq} SQUAD`,
      cadets: cadets.filter(c => norm(c.sqd) === sq),
    }));
}

const sortCadetsForBoard = (cadets) => [...cadets].sort((a, b) => {
  const pa = POSITION_ORDER.indexOf(positionCode(a.duty).toUpperCase());
  const pb = POSITION_ORDER.indexOf(positionCode(b.duty).toUpperCase());
  const ra = pa === -1 ? POSITION_ORDER.length : pa;
  const rb = pb === -1 ? POSITION_ORDER.length : pb;
  if (ra !== rb) return ra - rb;
  return (a.last || "").localeCompare(b.last || "");
});

const BLOCK_HEADER_FILL = {
  hq:     "FFFFC000", // gold — matches the tracker's company HQ/LDR block
  staff:  "FFFFD966", // light gold — staff sections (S1-S5, PA, etc.)
  unassigned: "FFD9D9D9",
};
const PLATOON_PALETTE = ["FFF4CC99","FFB8E0F5","FFC8E6C0","FFE3C6E8","FFBFDCE6"];

function blockHeaderFill(key, platoonIndex) {
  if (key === "HQ") return BLOCK_HEADER_FILL.hq;
  if (key === "") return BLOCK_HEADER_FILL.unassigned;
  if (!isNaN(parseInt(key, 10))) return PLATOON_PALETTE[platoonIndex % PLATOON_PALETTE.length];
  return BLOCK_HEADER_FILL.staff;
}

const TIMES = "Times New Roman";
function mergeRow(ws, rowNum, text, { fill, fontSize=14, bold=true, color="FF000000" } = {}) {
  ws.mergeCells(`A${rowNum}:E${rowNum}`);
  const cell = ws.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { name:TIMES, size:fontSize, bold, color:{ argb:color } };
  cell.alignment = { horizontal:"center", vertical:"middle" };
  if (fill) cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:fill } };
  cell.border = { top:{style:"thin"}, bottom:{style:"thin"}, left:{style:"thin"}, right:{style:"thin"} };
}

// Builds one company/day status-board worksheet in wb. dayReport may be null
// (no formation report filed for that date yet — every cadet shows NR).
function buildAccountabilityStatusSheet(wb, sheetName, companyLabel, coRoster, dayReport, dateLabel) {
  const ws = wb.addWorksheet(sheetName.slice(0, 31));
  ws.columns = [{ width:11 }, { width:9 }, { width:26 }, { width:8 }, { width:10 }];

  mergeRow(ws, 1, `${companyLabel.toUpperCase()} — MORNING FORMATION ACCOUNTABILITY`,
    { fill:"FFFFC000", fontSize:18 });
  ws.getRow(1).height = 26;
  mergeRow(ws, 2, dayReport ? dateLabel : `${dateLabel} — NO FORMATION REPORT ON FILE`,
    { fill:"FFD9D9D9", fontSize:12, bold:true });
  ws.getRow(2).height = 18;

  // Status legend + company-wide count summary (5 columns = 5 statuses)
  const counts = { P:0, EA:0, UA:0, LV:0, SAB:0 };
  coRoster.forEach(c => { const { code } = getCadetDayStatus(c, dayReport); if (counts[code] !== undefined) counts[code]++; });
  const codes = ["P","EA","UA","LV","SAB"];
  const legendRow = ws.addRow(codes);
  legendRow.eachCell((cell, i) => {
    const code = codes[i-1];
    cell.font = { name:"Calibri", size:11, bold:true, color:{ argb: (code==="UA"||code==="SAB") ? "FFFFFFFF" : "FF000000" } };
    cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb: STATUS_BOARD_FILL[code] } };
    cell.alignment = { horizontal:"center", vertical:"middle" };
    cell.border = { top:{style:"thin"}, bottom:{style:"thin"}, left:{style:"thin"}, right:{style:"thin"} };
  });
  const countRow = ws.addRow(codes.map(c => counts[c]));
  countRow.eachCell(cell => {
    cell.font = { name:"Calibri", size:11, bold:true };
    cell.alignment = { horizontal:"center", vertical:"middle" };
    cell.border = { top:{style:"thin"}, bottom:{style:"thin"}, left:{style:"thin"}, right:{style:"thin"} };
  });
  ws.addRow([]); // spacer

  const blocks = groupRosterForBoard(coRoster);
  let platoonIndex = 0;
  blocks.forEach(({ key, cadets, labelOf }) => {
    const isNumberedPlatoon = !isNaN(parseInt(key, 10));
    const headerRow = ws.rowCount + 1;
    mergeRow(ws, headerRow, labelOf(key, companyLabel), { fill: blockHeaderFill(key, platoonIndex), fontSize:14 });
    ws.getRow(headerRow).height = 20;
    if (isNumberedPlatoon) platoonIndex++;

    const colHeaderRow = ws.addRow(["Posit.","Rank","Name (Last, First)","Status","Room"]);
    colHeaderRow.eachCell(cell => {
      cell.font = { name:TIMES, size:11, bold:true };
      cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:"FFD9D9D9" } };
      cell.alignment = { horizontal:"center", vertical:"middle" };
      cell.border = { top:{style:"thin"}, bottom:{style:"thin"}, left:{style:"thin"}, right:{style:"thin"} };
    });

    const squadGroups = isNumberedPlatoon ? groupBySquad(cadets) : [{ label:null, cadets }];
    squadGroups.forEach(({ label, cadets: sqCadets }) => {
      if (label) {
        const sqRow = ws.rowCount + 1;
        mergeRow(ws, sqRow, label, { fontSize:10, bold:true, fill:"FFF2F2F2" });
        ws.getRow(sqRow).height = 14;
      }
      sortCadetsForBoard(sqCadets).forEach(cadet => {
        const { code } = getCadetDayStatus(cadet, dayReport);
        const row = ws.addRow([
          positionCode(cadet.duty), rankCode(cadet.rank),
          `${cadet.last || ""}, ${cadet.first || ""}`, code, cadet.room || "",
        ]);
        row.height = 15;
        row.eachCell((cell, i) => {
          cell.font = { name:"Calibri", size:11 };
          cell.alignment = { horizontal: i===3 ? "left" : "center", vertical:"middle" };
          cell.border = { top:{style:"thin"}, bottom:{style:"thin"}, left:{style:"thin"}, right:{style:"thin"} };
          if (i === 4) cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:"FFD9D9D9" } };
        });
      });
    });
    ws.addRow([]); // spacer between blocks
  });
  return ws;
}

// Human-readable date label, e.g. "Monday, August 25, 2026"
function fmtBoardDate(iso) {
  if (!iso) return "No Date";
  try { return new Date(iso.split("T")[0] + "T12:00:00").toLocaleDateString("en-US", { weekday:"long", year:"numeric", month:"long", day:"numeric" }); }
  catch { return iso; }
}

// Quick-download: builds the status board for the most recent formation
// report on file for this company (used by the "⬇" one-click buttons).
async function downloadCompanyAccountabilityMaster(company, coRoster, allAcctSubs) {
  const toDateOnly = (str) => (str || "").split("T")[0];
  const dates = [...new Set(allAcctSubs.map(s => toDateOnly(s.details?.formationDate || s.date)).filter(Boolean))].sort();
  const latest = dates[dates.length - 1];
  const dayReport = latest ? allAcctSubs.find(s => toDateOnly(s.details?.formationDate || s.date) === latest) : null;

  const wb = new ExcelJS.Workbook();
  buildAccountabilityStatusSheet(wb, "Status Board", companyShortTitle(company), coRoster, dayReport, fmtBoardDate(latest));
  await downloadWorkbook(wb, `${company.replace(/ /g,"_")}_Accountability_${latest || "NoReports"}.xlsx`);
}

// S1-only: one combined workbook with every company's status board for its
// own latest date, plus an OVERVIEW tab (personnel + status counts by company).
async function downloadBattalionAccountabilityWorkbook(roster, submissions, clearedSubmissions) {
  const toDateOnly = (str) => (str || "").split("T")[0];
  const isMorning = s => s.type === "Accountability" && (s.details?.formationType || "").toLowerCase().includes("morning");
  const wb = new ExcelJS.Workbook();

  // First pass: compute each company's data (without adding sheets yet) so
  // the OVERVIEW tab can be inserted first, matching the tracker's tab order.
  const perCompany = CO_LIST.map(co => {
    const coRoster = roster.filter(r => r.unit === co);
    const acctSubs = [...submissions.filter(s => s.unit === co && isMorning(s)), ...clearedSubmissions.filter(s => s.unit === co && isMorning(s))];
    const dates = [...new Set(acctSubs.map(s => toDateOnly(s.details?.formationDate || s.date)).filter(Boolean))].sort();
    const latest = dates[dates.length - 1];
    const dayReport = latest ? acctSubs.find(s => toDateOnly(s.details?.formationDate || s.date) === latest) : null;
    const counts = { P:0, EA:0, UA:0, LV:0, SAB:0 };
    coRoster.forEach(c => { const { code } = getCadetDayStatus(c, dayReport); if (counts[code] !== undefined) counts[code]++; });
    return { co, coRoster, dayReport, latest, counts };
  });
  const overviewRows = perCompany.map(({ co, coRoster, latest, counts }) =>
    ({ Company: co, Cadets: coRoster.length, Updated: latest || "—", ...counts }));

  const ovWs = wb.addWorksheet("OVERVIEW");
  ovWs.columns = [
    { header:"Company", key:"Company", width:16 }, { header:"Cadets", key:"Cadets", width:10 },
    { header:"Updated", key:"Updated", width:14 }, { header:"P", key:"P", width:8 },
    { header:"EA", key:"EA", width:8 }, { header:"UA", key:"UA", width:8 },
    { header:"LV", key:"LV", width:8 }, { header:"SAB", key:"SAB", width:8 },
  ];
  ovWs.getRow(1).eachCell(cell => {
    cell.font = { name:TIMES, size:12, bold:true };
    cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb:"FFFFC000" } };
    cell.alignment = { horizontal:"center", vertical:"middle" };
  });
  overviewRows.forEach(r => {
    const row = ovWs.addRow(r);
    row.eachCell(cell => { cell.font = { name:"Calibri", size:11 }; cell.alignment = { horizontal:"center" }; });
  });

  perCompany.forEach(({ co, coRoster, dayReport, latest }) => {
    buildAccountabilityStatusSheet(wb, companyShortTitle(co), companyShortTitle(co), coRoster, dayReport, fmtBoardDate(latest));
  });

  await downloadWorkbook(wb, `S1_Battalion_Accountability_${new Date().toISOString().split("T")[0]}.xlsx`);
}


// ─────────────────────────────────────────────
//  SHARED UI
// ─────────────────────────────────────────────
const inputBase = {
  width:"100%", background:T.bg, border:`1px solid ${T.border}`,
  color:T.light, fontFamily:sans, fontSize:14, padding:"10px 14px",
  boxSizing:"border-box", outline:"none", borderRadius:6, transition:"border-color 0.15s",
};
function Inp({ style, ...props }) {
  const [f, setF] = useState(false);
  return <input {...props} style={{ ...inputBase, ...style, borderColor:f?T.accent:T.border }}
    onFocus={()=>setF(true)} onBlur={()=>setF(false)} />;
}
function Sel({ children, style, ...props }) {
  const [f, setF] = useState(false);
  return <select {...props} style={{ ...inputBase, cursor:"pointer", ...style, borderColor:f?T.accent:T.border }}
    onFocus={()=>setF(true)} onBlur={()=>setF(false)}>{children}</select>;
}
function Ta({ style, ...props }) {
  const [f, setF] = useState(false);
  return <textarea {...props} style={{ ...inputBase, resize:"vertical", minHeight:82, ...style, borderColor:f?T.accent:T.border }}
    onFocus={()=>setF(true)} onBlur={()=>setF(false)} />;
}
function Field({ label, required, children, style }) {
  return (
    <div style={{ marginBottom:18, ...style }}>
      <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:600, letterSpacing:0.8,
        marginBottom:7, textTransform:"uppercase" }}>
        {label}{required&&<span style={{ color:T.red, marginLeft:3 }}>*</span>}
      </div>
      {children}
    </div>
  );
}
function SecHead({ n, label }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20,
      borderBottom:`1px solid ${T.border}`, paddingBottom:12 }}>
      <div style={{ width:26, height:26, borderRadius:"50%", background:T.olive, border:`1px solid ${T.accent}50`,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontFamily:mono, color:T.accent, fontSize:11, fontWeight:700, flexShrink:0 }}>{n}</div>
      <span style={{ fontFamily:sans, color:T.tan, fontSize:12, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{label}</span>
    </div>
  );
}
function AbsenceAutoComplete({ value, roster, idx, onSelect }) {
  const [show, setShow] = useState(false);
  const [matches, setMatches] = useState([]);
  React.useEffect(()=>{
    if(value.length>=2){
      const q=value.toUpperCase();
      const m=roster.filter(r=>r.last?.toUpperCase().startsWith(q)||`${r.last} ${r.first}`.toUpperCase().includes(q)).slice(0,8);
      setMatches(m); setShow(m.length>0);
    } else { setMatches([]); setShow(false); }
  },[value,roster]);
  if(!show) return null;
  return (
    <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:50,
      background:T.card, border:`1px solid ${T.accent}`, borderRadius:"0 0 6px 6px",
      boxShadow:"0 8px 24px #00000060", maxHeight:200, overflow:"auto" }}>
      {matches.map((c,i)=>(
        <div key={i} onMouseDown={()=>{ onSelect(c,idx); setShow(false); }}
          style={{ padding:"8px 14px", cursor:"pointer", fontFamily:sans, fontSize:13,
            borderBottom:`1px solid ${T.border}`, display:"flex", gap:10, alignItems:"center" }}
          onMouseEnter={e=>e.currentTarget.style.background=T.accent+"20"}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          <span style={{ fontFamily:mono, color:T.tan, fontSize:11, minWidth:40 }}>{c.rank}</span>
          <span style={{ color:T.light }}>{c.last}, {c.first}</span>
          <span style={{ color:T.dim, fontSize:11, marginLeft:"auto" }}>{c.unit} · Rm {c.room}</span>
        </div>
      ))}
    </div>
  );
}

function Badge({ status }) {
  const c = STATUS_C[status]||T.dim;
  return <span style={{ background:c+"20", color:c, border:`1px solid ${c}40`,
    fontFamily:mono, fontSize:10, padding:"3px 9px", letterSpacing:1.5, fontWeight:700, borderRadius:4 }}>{status}</span>;
}
function Stat({ label, value, color }) {
  return (
    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8,
      padding:"18px 22px", flex:1, minWidth:110, borderTop:`3px solid ${color}` }}>
      <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:600, marginBottom:6 }}>{label}</div>
      <div style={{ fontFamily:mono, color, fontSize:28, fontWeight:700 }}>{value}</div>
    </div>
  );
}
function Btn({ children, onClick, variant="primary", style, disabled }) {
  const styles = {
    primary:   { background:T.accent, color:"#0f1410", border:"none" },
    secondary: { background:"transparent", color:T.dim, border:`1px solid ${T.border}` },
    ghost:     { background:T.card, color:T.light, border:`1px solid ${T.border}` },
    danger:    { background:"transparent", color:T.red, border:`1px solid ${T.red}40` },
    green:     { background:T.green, color:"#a8d890", border:`1px solid #4a8a3060` },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily:sans, fontSize:13, fontWeight:700, letterSpacing:0.5,
      padding:"10px 22px", borderRadius:6, cursor:disabled?"not-allowed":"pointer",
      opacity:disabled?0.5:1, transition:"all 0.15s", ...styles[variant], ...style,
    }}>{children}</button>
  );
}
function Card({ children, style }) {
  return <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:24, marginBottom:16, ...style }}>{children}</div>;
}

// ─────────────────────────────────────────────
//  S1 GATE
// ─────────────────────────────────────────────
function S1Gate({ onUnlock }) {
  const [pw, setPw]       = useState("");
  const [err, setErr]     = useState(false);
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const attempt = async () => {
    if (!pw || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/s1`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) { onUnlock(); return; }
    } catch(e) { console.error(e); }
    setLoading(false);
    setErr(true); setShake(true); setPw("");
    setTimeout(() => setShake(false), 500);
  };
  return (
    <div style={{ minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-10px)}60%{transform:translateX(10px)}}`}</style>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"48px 52px",
        maxWidth:420, width:"100%", textAlign:"center", borderTop:`3px solid ${T.accent}`,
        animation:shake?"shake 0.4s":"none", boxShadow:"0 20px 60px #00000060" }}>
        <div style={{ width:56, height:56, borderRadius:"50%", background:T.olive, border:`2px solid ${T.accent}`,
          display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px",
          fontFamily:mono, color:T.accent, fontSize:20, fontWeight:700 }}>S1</div>
        <div style={{ fontFamily:sans, color:T.light, fontSize:20, fontWeight:700, marginBottom:4 }}>S-1 Dashboard</div>
        <div style={{ fontFamily:sans, color:T.dim, fontSize:13, marginBottom:32 }}>Restricted — Authorized Personnel Only</div>
        <Field label="Access Code" required>
          <Inp type="password" value={pw} onChange={e=>{ setPw(e.target.value); setErr(false); }}
            onKeyDown={e=>e.key==="Enter"&&attempt()} placeholder="Enter code"
            style={{ textAlign:"center", fontSize:18, letterSpacing:6 }} />
        </Field>
        {err && <div style={{ fontFamily:sans, color:T.red, fontSize:12, marginBottom:14 }}>⚠ Invalid access code</div>}
        <Btn onClick={attempt} disabled={!pw||loading} style={{ width:"100%", padding:"13px", fontSize:14 }}>{loading?"Verifying…":"Authenticate →"}</Btn>
        <div style={{ fontFamily:sans, color:T.faint, fontSize:11, marginTop:20 }}>Unauthorized access is prohibited</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  ROSTER IMPORT MODAL  (S1 only)
// ─────────────────────────────────────────────
function RosterImportModal({ onClose, onImport }) {
  const [tab, setTab] = useState("paste"); // "paste" | "file"
  const [pasteText, setPasteText] = useState("");
  const [preview, setPreview] = useState(null);  // { cadets, errors }
  const [importMode, setImportMode] = useState("replace"); // "replace" | "append"
  const [fileName, setFileName] = useState("");
  const fileRef = useRef();

  const handleParsePaste = () => {
    const result = parseTextBlock(pasteText);
    setPreview(result);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    parseSpreadsheet(file, result => setPreview(result));
  };

  const handleImport = () => {
    if (!preview?.cadets?.length) return;
    onImport(preview.cadets, importMode);
    onClose();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000090", zIndex:200,
      display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12,
        width:"100%", maxWidth:780, maxHeight:"90vh", overflow:"auto",
        boxShadow:"0 24px 80px #000000a0" }}>
        {/* Header */}
        <div style={{ padding:"22px 28px", borderBottom:`1px solid ${T.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontFamily:sans, color:T.light, fontSize:18, fontWeight:700 }}>Import Cadet Roster</div>
            <div style={{ fontFamily:sans, color:T.dim, fontSize:12, marginTop:3 }}>
              Required format: <span style={{ fontFamily:mono, color:T.tan }}>LastName, FirstName, MIName, BN, UNIT, PLT, RANK, DUTY, CADET_YEAR, Email_Address</span>, plus optional <span style={{ fontFamily:mono, color:T.tan }}>SQD, ROOM</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:T.dim,
            fontSize:22, cursor:"pointer", lineHeight:1 }}>✕</button>
        </div>

        <div style={{ padding:"24px 28px" }}>
          {/* Format example */}
          <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:6,
            padding:"14px 18px", marginBottom:20, fontFamily:mono, fontSize:12, color:T.dim, lineHeight:2 }}>
            <span style={{ color:T.faint }}>Column order (tab-separated, one cadet per line):</span><br/>
            <span style={{ color:T.dim, fontSize:11 }}>LastName  FirstName  MIName  BN  UNIT  PLT  RANK  DUTY  CADET_YEAR  Email_Address  [SQD]  [ROOM]</span><br/>
            <span style={{ color:T.tan }}>HARRIS{"\t"}MARCUS{"\t"}M{"\t"}1{"\t"}ALPHA CO{"\t"}1{"\t"}SGT{"\t"}Squad Leader{"\t"}3Y1{"\t"}mharris@norwich.edu{"\t"}1{"\t"}RH305</span><br/>
            <span style={{ color:T.tan }}>NGUYEN{"\t"}PETER{"\t"}{"\t"}1{"\t"}BRAVO CO{"\t"}2{"\t"}CPL{"\t"}Squad Member{"\t"}2Y1{"\t"}pnguyen@norwich.edu{"\t"}2{"\t"}RH210</span><br/>
            <span style={{ color:T.faint, fontSize:11 }}>Tip: Copy directly from your official roster spreadsheet — columns must be in the order shown above. UNIT must be filled in for every cadet (e.g. "ALPHA CO" or just "A") — a blank UNIT column is why a company's accountability lists can come up empty. SQD and ROOM are optional but required for the squad-level breakdown on the accountability status board.</span>
          </div>

          {/* Tab switcher */}
          <div style={{ display:"flex", gap:6, marginBottom:20 }}>
            {[["paste","✏ Paste Text List"],["file","📂 Upload Excel / CSV"]].map(([key,label])=>(
              <button key={key} onClick={()=>{ setTab(key); setPreview(null); setFileName(""); }}
                style={{ padding:"8px 18px", borderRadius:6, cursor:"pointer", fontFamily:sans,
                  fontSize:13, fontWeight:600, border:`1px solid ${tab===key?T.accent:T.border}`,
                  background:tab===key?T.accent+"20":"transparent",
                  color:tab===key?T.accent:T.dim, transition:"all 0.15s" }}>{label}</button>
            ))}
          </div>

          {/* Paste tab */}
          {tab==="paste" && (
            <div>
              <Field label="Paste Roster List">
                <Ta value={pasteText} onChange={e=>{ setPasteText(e.target.value); setPreview(null); }}
                  placeholder={"HARRIS\tMARCUS\tM\t1\tALPHA CO\t1\tSGT\tSquad Leader\t3Y1\tmharris@norwich.edu\nNGUYEN\tPETER\t\t1\tBRAVO CO\t2\tCPL\tSquad Member\t2Y1\tpnguyen@norwich.edu"}
                  style={{ minHeight:160, fontFamily:mono, fontSize:13 }} />
              </Field>
              <Btn onClick={handleParsePaste} disabled={!pasteText.trim()}>Preview Import →</Btn>
            </div>
          )}

          {/* File tab */}
          {tab==="file" && (
            <div>
              <div style={{ border:`2px dashed ${T.border}`, borderRadius:8, padding:"32px",
                textAlign:"center", cursor:"pointer", marginBottom:16 }}
                onClick={()=>fileRef.current?.click()}>
                <div style={{ fontSize:32, marginBottom:8 }}>📊</div>
                <div style={{ fontFamily:sans, color:T.dim, fontSize:14, marginBottom:4 }}>
                  {fileName || "Click to upload .xlsx or .csv"}
                </div>
                <div style={{ fontFamily:sans, color:T.faint, fontSize:12 }}>
                  Columns (in order): LastName · FirstName · MIName · BN · UNIT · PLT · RANK · DUTY · CADET_YEAR · Email_Address
                </div>
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }}
                onChange={handleFile} />
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div style={{ marginTop:24 }}>
              <div style={{ display:"flex", gap:12, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
                <div style={{ background:T.green, color:"#a8d890", border:`1px solid #4a8a3060`,
                  borderRadius:6, padding:"8px 16px", fontFamily:sans, fontSize:13, fontWeight:700 }}>
                  ✓ {preview.cadets.length} cadets ready to import
                </div>
                {preview.errors.length>0 && (
                  <div style={{ background:T.red+"20", color:"#d88070", border:`1px solid ${T.red}40`,
                    borderRadius:6, padding:"8px 16px", fontFamily:sans, fontSize:13, fontWeight:700 }}>
                    ⚠ {preview.errors.length} line{preview.errors.length>1?"s":""} could not be parsed
                  </div>
                )}
              </div>

              {preview.errors.length>0 && (
                <div style={{ background:T.bg, border:`1px solid ${T.red}30`, borderRadius:6,
                  padding:"12px 16px", marginBottom:16, maxHeight:120, overflow:"auto" }}>
                  <div style={{ fontFamily:sans, color:T.red, fontSize:11, fontWeight:700, marginBottom:8 }}>PARSE ERRORS — these lines were skipped:</div>
                  {preview.errors.map((e,i)=>(
                    <div key={i} style={{ fontFamily:mono, color:T.dim, fontSize:11, marginBottom:4 }}>
                      Line {e.line}: <span style={{ color:"#d08070" }}>{e.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Preview table */}
              <div style={{ border:`1px solid ${T.border}`, borderRadius:6, overflow:"hidden", marginBottom:16, maxHeight:240, overflowY:"auto", overflowX:"auto" }}>
                <div style={{ display:"grid", gridTemplateColumns:"110px 90px 60px 50px 110px 50px 70px 120px 70px 180px", minWidth:1090,
                  padding:"8px 14px", background:T.card2, fontFamily:sans,
                  color:T.dim, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5,
                  position:"sticky", top:0 }}>
                  <span>Last</span><span>First</span><span>MI</span><span>BN</span><span>Unit</span><span>Plt</span><span>Rank</span><span>Duty</span><span>Year</span><span>Email</span>
                </div>
                {preview.cadets.map((c,i)=>(
                  <div key={i} style={{ display:"grid", gridTemplateColumns:"110px 90px 60px 50px 110px 50px 70px 120px 70px 180px", minWidth:1090,
                    padding:"8px 14px", borderTop:`1px solid ${T.border}`,
                    background:i%2===0?"transparent":T.card2 }}>
                    <span style={{ fontFamily:sans, color:T.accent, fontSize:12 }}>{c.last}</span>
                    <span style={{ fontFamily:sans, color:T.light, fontSize:12 }}>{c.first}</span>
                    <span style={{ fontFamily:sans, color:T.dim, fontSize:11 }}>{c.mi}</span>
                    <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.bn}</span>
                    <span style={{ fontFamily:sans, color:c.unit?T.dim:"#c06050", fontSize:11 }}>{c.unit||"⚠ UNKNOWN"}</span>
                    <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.plt}</span>
                    <span style={{ fontFamily:mono, color:T.tan, fontSize:12, fontWeight:700 }}>{c.rank}</span>
                    <span style={{ fontFamily:sans, color:T.dim, fontSize:11 }}>{c.duty}</span>
                    <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.cadetYear}</span>
                    <span style={{ fontFamily:sans, color:T.faint, fontSize:11 }}>{c.email}</span>
                  </div>
                ))}
              </div>

              {/* Import mode */}
              <Field label="Import Mode">
                <div style={{ display:"flex", gap:10 }}>
                  {[["replace","Replace Roster"],["append","Append to Roster"]].map(([key,label])=>(
                    <button key={key} onClick={()=>setImportMode(key)} style={{
                      flex:1, padding:"11px", borderRadius:6, cursor:"pointer",
                      border:`2px solid ${importMode===key?T.accent:T.border}`,
                      background:importMode===key?T.accent+"20":"transparent",
                      fontFamily:sans, fontSize:13, fontWeight:700,
                      color:importMode===key?T.accent:T.dim, transition:"all 0.15s" }}>
                      {key==="replace"?"🔄 "+label:"➕ "+label}
                    </button>
                  ))}
                </div>
                <div style={{ fontFamily:sans, color:T.faint, fontSize:11, marginTop:8 }}>
                  {importMode==="replace"
                    ? "⚠ This will clear the existing roster and replace with imported data."
                    : "New cadets will be added to the existing roster."}
                </div>
              </Field>

              <div style={{ display:"flex", gap:10 }}>
                <Btn onClick={handleImport} disabled={!preview.cadets.length}
                  style={{ padding:"13px 36px", fontSize:14 }}>
                  Confirm Import ({preview.cadets.length} cadets) →
                </Btn>
                <Btn variant="secondary" onClick={()=>setPreview(null)}>Reset</Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  CONFIRM DIALOG
// ─────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"#00000090", zIndex:300,
      display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10,
        padding:"32px 36px", maxWidth:420, width:"100%", textAlign:"center",
        boxShadow:"0 20px 60px #000000a0", borderTop:`3px solid ${T.red}` }}>
        <div style={{ fontSize:28, marginBottom:12 }}>⚠</div>
        <div style={{ fontFamily:sans, color:T.light, fontSize:15, fontWeight:600,
          marginBottom:24, lineHeight:1.6 }}>{message}</div>
        <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
          <Btn variant="danger" onClick={onConfirm} style={{ padding:"10px 28px" }}>Confirm Remove</Btn>
          <Btn variant="secondary" onClick={onCancel} style={{ padding:"10px 28px" }}>Cancel</Btn>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  ACCOUNTABILITY DATE VIEWER
// ─────────────────────────────────────────────
function AccountabilityCalendarModal({ allAcctSubs, coRoster, companyName, onClose }) {
  // formationDate is stored as datetime-local ("2026-08-19T06:00"); normalize to date-only for comparison
  const toDateOnly = (str) => (str || "").split("T")[0];

  const initDate = useMemo(() => {
    const dates = allAcctSubs.map(s => toDateOnly(s.details?.formationDate || s.date)).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : "";
  }, [allAcctSubs]);

  const [selected, setSelected] = useState(initDate);

  const reportDates = useMemo(() => new Set(
    allAcctSubs.map(s => toDateOnly(s.details?.formationDate || s.date)).filter(Boolean)
  ), [allAcctSubs]);

  const dayReport = selected
    ? allAcctSubs.find(s => toDateOnly(s.details?.formationDate || s.date) === selected)
    : null;

  const hasReport = selected && reportDates.has(selected);

  const counts = useMemo(() => {
    const c = { P:0, EA:0, UA:0, LV:0, SAB:0, NR:0 };
    coRoster.forEach(cadet => { const { code } = getCadetDayStatus(cadet, dayReport); if (c[code]!==undefined) c[code]++; });
    return c;
  }, [dayReport, coRoster]);

  const fmtLong = fmtBoardDate;

  const downloadDay = async () => {
    if (!dayReport) return;
    const wb = new ExcelJS.Workbook();
    buildAccountabilityStatusSheet(wb, "Status Board", companyName, coRoster, dayReport, fmtLong(selected));
    await downloadWorkbook(wb, `${companyName.replace(/ /g,"_")}_Accountability_${selected}.xlsx`);
  };

  const BADGE = {
    P:   { bg:"#2a6a2a30", col:"#a8e8a8", label:"Present"       },
    EA:  { bg:"#5a2a6a30", col:"#e0a8f8", label:"Excused"       },
    UA:  { bg:"#8b1a1a30", col:"#f8a8a8", label:"Unexcused"     },
    LV:  { bg:"#6a5a2a30", col:"#e8d8a8", label:"Leave"         },
    SAB: { bg:"#2a4a6a30", col:"#a8c8e8", label:"Study Abroad"  },
    NR:  { bg:"#3a3a3a30", col:"#909090", label:"No Report"     },
  };

  return (
    <div
      onMouseDown={e => { if (e.target===e.currentTarget) onClose(); }}
      style={{ position:"fixed", inset:0, zIndex:600, background:"rgba(0,0,0,0.82)",
        display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>

      <div style={{ width:"100%", maxWidth:520, maxHeight:"92vh", overflowY:"auto",
        background:T.card, border:`1px solid ${T.border}`, borderRadius:12,
        boxShadow:"0 32px 80px rgba(0,0,0,0.85)" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
          padding:"20px 24px 0", marginBottom:20 }}>
          <div>
            <div style={{ fontFamily:sans, color:T.dim, fontSize:11, letterSpacing:1.2,
              textTransform:"uppercase", marginBottom:3 }}>{companyName}</div>
            <div style={{ fontFamily:sans, color:T.light, fontSize:16, fontWeight:700 }}>
              Accountability by Date
            </div>
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:`1px solid ${T.border}`,
            borderRadius:8, color:T.dim, width:32, height:32, cursor:"pointer", fontSize:16,
            display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>

        {/* Date input */}
        <div style={{ padding:"0 24px 20px" }}>
          <label style={{ display:"block", fontFamily:sans, color:T.dim, fontSize:11,
            fontWeight:700, letterSpacing:0.8, textTransform:"uppercase", marginBottom:8 }}>
            Select Date
          </label>
          <input
            type="date"
            value={selected}
            onChange={e => setSelected(e.target.value)}
            style={{ width:"100%", padding:"10px 14px", borderRadius:8,
              background:T.card2, border:`1px solid ${T.border}`, color:T.light,
              fontFamily:sans, fontSize:15, outline:"none", cursor:"pointer",
              colorScheme:"dark" }}
          />
          {selected && !hasReport && (
            <div style={{ marginTop:10, fontFamily:sans, color:T.gold, fontSize:13 }}>
              No Morning Formation report found for this date.
            </div>
          )}
          {allAcctSubs.length > 0 && (
            <div style={{ marginTop:8, fontFamily:sans, color:T.faint, fontSize:11 }}>
              {reportDates.size} report{reportDates.size!==1?"s":""} on file for {companyName}
            </div>
          )}
        </div>

        {/* Day detail */}
        {hasReport && dayReport && (
          <div style={{ borderTop:`1px solid ${T.border}` }}>

            {/* Day header */}
            <div style={{ padding:"16px 24px 12px", borderBottom:`1px solid ${T.border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:10 }}>
                <div style={{ fontFamily:sans, color:T.tan, fontSize:14, fontWeight:700 }}>
                  {fmtLong(selected)}
                </div>
                <button
                  onClick={downloadDay}
                  style={{ flexShrink:0, padding:"6px 14px", borderRadius:6,
                    background:T.accent, color:"#0f1410", fontFamily:sans,
                    fontSize:12, fontWeight:700, border:"none", cursor:"pointer" }}>
                  ⬇ Download
                </button>
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <span style={{ background:T.accent+"20", color:T.accent, fontFamily:sans, fontSize:11,
                  padding:"3px 10px", borderRadius:20, fontWeight:700, border:`1px solid ${T.accent}30` }}>
                  {dayReport.details?.formationType || "Morning Formation"}
                </span>
                {dayReport.details?.allAccounted
                  ? <span style={{ background:"#2a6a2a40", color:"#a8e8a8", fontFamily:sans, fontSize:11, padding:"3px 10px", borderRadius:20, fontWeight:700 }}>✓ All Accounted</span>
                  : <span style={{ background:"#8b1a1a40", color:"#f8a8a8", fontFamily:sans, fontSize:11, padding:"3px 10px", borderRadius:20, fontWeight:700 }}>
                      ✗ {(dayReport.details?.absences||[]).length} absence{(dayReport.details?.absences||[]).length!==1?"s":""}
                    </span>
                }
              </div>
            </div>

            {coRoster.length > 0 ? (
              <>
                {/* Summary pills */}
                {counts && (
                  <div style={{ display:"flex", gap:8, padding:"12px 24px", flexWrap:"wrap" }}>
                    {["P","EA","UA","LV","SAB","NR"].filter(code => counts[code]>0).map(code => (
                      <span key={code} style={{ background:BADGE[code].bg.replace("30","40"), color:BADGE[code].col, fontFamily:sans,
                        fontSize:12, padding:"4px 14px", borderRadius:20, fontWeight:700 }}>
                        {counts[code]} {BADGE[code].label}
                      </span>
                    ))}
                  </div>
                )}

                {/* Roster list */}
                <div style={{ maxHeight:340, overflowY:"auto", borderTop:`1px solid ${T.border}` }}>
                  <div style={{ display:"grid", gridTemplateColumns:"44px 1fr auto",
                    padding:"7px 20px", background:T.card2, gap:10,
                    fontFamily:sans, color:T.faint, fontSize:10, fontWeight:700,
                    letterSpacing:0.8, textTransform:"uppercase" }}>
                    <span>Rank</span><span>Name</span><span>Status</span>
                  </div>
                  {coRoster.map((cadet, i) => {
                    const { code, reason } = getCadetDayStatus(cadet, dayReport);
                    const b = BADGE[code] || { bg:"transparent", col:T.dim, label:"—" };
                    return (
                      <div key={i} style={{
                        display:"grid", gridTemplateColumns:"44px 1fr auto",
                        alignItems:"center", gap:10, padding:"9px 20px",
                        background: i%2===0 ? T.bg : T.surface,
                        borderBottom:`1px solid ${T.border}`,
                      }}>
                        <span style={{ fontFamily:mono, color:T.tan, fontSize:11, fontWeight:700 }}>{cadet.rank}</span>
                        <div>
                          <span style={{ fontFamily:sans, color:T.light, fontSize:13, fontWeight:600 }}>
                            {cadet.last}, {cadet.first}
                          </span>
                          {reason && (
                            <div style={{ fontFamily:sans, color:T.dim, fontSize:10, marginTop:1 }}>{reason}</div>
                          )}
                        </div>
                        <span style={{ fontFamily:sans, fontSize:11, padding:"3px 10px",
                          borderRadius:20, background:b.bg, color:b.col, fontWeight:700,
                          whiteSpace:"nowrap" }}>
                          {b.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ padding:"16px 24px", fontFamily:sans, color:T.gold, fontSize:12 }}>
                No roster loaded for {companyName} — S1 must upload a roster to see per-cadet breakdown.
              </div>
            )}
          </div>
        )}

        <div style={{ height:8 }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  DASHBOARD
// ─────────────────────────────────────────────
function Dashboard({ submissions, setSubmissions, clearedSubmissions, setClearedSubs, roster, setRoster, onLock, rosterNotifications, setRosterNotifications }) {
  const [filter, setFilter]               = useState("ALL");
  const [showImport, setShowImport]       = useState(false);
  const [dashTab, setDashTab]             = useState("submissions");
  const [rosterSearch, setRosterSearch]   = useState("");
  const [rosterCo, setRosterCo]           = useState("ALL");
  const [confirm, setConfirm]             = useState(null);
  const [selected, setSelected]           = useState(new Set());
  const [clearedFilter, setClearedFilter] = useState("ALL");
  const [expandedSub, setExpandedSub]       = useState(null);
  const [companyFilter, setCompanyFilter]   = useState("ALL");
  const [selectedCadet, setSelectedCadet]   = useState(null);
  const [editingIdx, setEditingIdx]         = useState(null);
  const [editForm, setEditForm]             = useState({});
  const [showAddCadet, setShowAddCadet]     = useState(false);
  const [newCadetForm, setNewCadetForm]     = useState({
    a:"", username:"", last:"", first:"", mi:"", gen:"", bn:"", unit:"",
    plt:"", sqd:"", rank:"", duty:"", cadetYear:"", email:"", room:"", arrived:"N"
  });
  const [calendarCo, setCalendarCo]         = useState(null); // company name → opens accountability calendar

  const types = ["ALL","Promotion","Award","Room Transfer","Accountability"];

  // ── Clear submission helpers ──
  const clearOne = (sub) => setConfirm({
    message: `Clear ${sub.id} (${sub.cadet}) to Cleared History?`,
    onConfirm: async () => {
      const clearedSub = { ...sub, clearedAt: new Date().toISOString() };
      setSubmissions(prev => prev.filter(s => s.id !== sub.id));
      setClearedSubs(prev => [...prev, clearedSub]);
      setConfirm(null);
      try {
        await fetch(`${API}/cleared-submissions`, {
          method: 'POST', headers: {'Content-Type':'application/json'},
          body: JSON.stringify(clearedSub),
        });
      } catch(e) { console.error('clearOne:', e); }
    },
  });

  const clearSelected = () => {
    const toMove = submissions.filter(s => selected.has(s.id));
    setConfirm({
      message: `Clear ${toMove.length} selected submission${toMove.length>1?"s":""} to Cleared History?`,
      onConfirm: async () => {
        const now = new Date().toISOString();
        const cleared = toMove.map(s => ({ ...s, clearedAt: now }));
        setSubmissions(prev => prev.filter(s => !selected.has(s.id)));
        setClearedSubs(prev => [...prev, ...cleared]);
        setSelected(new Set()); setConfirm(null);
        for (const s of cleared) {
          try {
            await fetch(`${API}/cleared-submissions`, {
              method: 'POST', headers: {'Content-Type':'application/json'},
              body: JSON.stringify(s),
            });
          } catch(e) { console.error('clearSelected:', e); }
        }
      },
    });
  };

  const clearAllSubs = () => setConfirm({
    message: `Clear all ${submissions.length} active submission${submissions.length>1?"s":""} to Cleared History?`,
    onConfirm: async () => {
      const now = new Date().toISOString();
      const toMove = submissions.map(s => ({ ...s, clearedAt: now }));
      setClearedSubs(prev => [...prev, ...toMove]);
      setSubmissions([]); setSelected(new Set()); setConfirm(null);
      for (const s of toMove) {
        try {
          await fetch(`${API}/cleared-submissions`, {
            method: 'POST', headers: {'Content-Type':'application/json'},
            body: JSON.stringify(s),
          });
        } catch(e) { console.error('clearAllSubs:', e); }
      }
    },
  });

  // ── Submission checkbox helpers ──
  const filtered = submissions
    .filter(s=>filter==="ALL"||s.type===filter)
    .filter(s=>companyFilter==="ALL"||s.unit===companyFilter);
  const toggleSelectSub  = (id) => setSelected(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleSelectAllSubs = () => {
    const ids = filtered.map(s=>s.id);
    const allSel = ids.every(id=>selected.has(id));
    setSelected(prev=>{ const n=new Set(prev); allSel?ids.forEach(id=>n.delete(id)):ids.forEach(id=>n.add(id)); return n; });
  };
  const allFilteredSubsSel = filtered.length>0 && filtered.every(s=>selected.has(s.id));

  const counts = {
    total:   submissions.length,
    pending: submissions.filter(s=>s.status==="PENDING").length,
    approved:submissions.filter(s=>s.status==="APPROVED").length,
    urgent:  submissions.filter(s=>s.priority==="URGENT").length,
  };

  // ── Roster helpers ──
  const handleImport = async (cadets, mode) => {
    if (mode==="replace") setRoster(cadets); else setRoster(prev=>[...prev,...cadets]);
    setSelected(new Set());
    try {
      await fetch(`${API}/roster/bulk`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ mode, cadets }),
      });
      const updated = await fetch(`${API}/roster`).then(r => r.json());
      setRoster(updated);
    } catch(e) { console.error('handleImport:', e); }
  };
  const filteredRoster = roster
    .map((c,idx)=>({...c,_idx:idx}))
    .filter(c=>rosterCo==="ALL"||c.unit===rosterCo)
    .filter(c=>!rosterSearch||`${c.rank} ${c.last} ${c.first} ${c.unit}`.toLowerCase().includes(rosterSearch.toLowerCase()));
  const toggleSelect    = (idx) => setSelected(prev=>{ const n=new Set(prev); n.has(idx)?n.delete(idx):n.add(idx); return n; });
  const toggleSelectAll = () => {
    const idxs=filteredRoster.map(c=>c._idx), allSel=idxs.every(i=>selected.has(i));
    setSelected(prev=>{ const n=new Set(prev); allSel?idxs.forEach(i=>n.delete(i)):idxs.forEach(i=>n.add(i)); return n; });
  };
  const allFilteredSelected = filteredRoster.length>0 && filteredRoster.every(c=>selected.has(c._idx));
  const removeSingle = (c) => setConfirm({ message:`Remove ${c.rank} ${c.last} from the roster? This cannot be undone.`, onConfirm: async ()=>{ setRoster(prev=>prev.filter((_,i)=>i!==c._idx)); setSelected(prev=>{ const n=new Set(prev); n.delete(c._idx); return n; }); setConfirm(null); if(c.id){ try{ await fetch(`${API}/roster/${c.id}`,{method:'DELETE'}); }catch(e){ console.error('removeSingle:',e); } } } });
  const removeSelected = () => { const cnt=selected.size; const toRemove=roster.filter((_,i)=>selected.has(i)); setConfirm({ message:`Remove ${cnt} selected cadet${cnt>1?"s":""} from the roster? This cannot be undone.`, onConfirm: async ()=>{ setRoster(prev=>prev.filter((_,i)=>!selected.has(i))); setSelected(new Set()); setConfirm(null); await Promise.all(toRemove.filter(c=>c.id).map(c=>fetch(`${API}/roster/${c.id}`,{method:'DELETE'}).catch(e=>console.error('removeSelected:',e)))); } }); };
  const clearCompany = (co) => { const toRemove=roster.filter(c=>c.unit===co); const cnt=toRemove.length; setConfirm({ message:`Remove all ${cnt} cadet${cnt>1?"s":""} from ${co}? This cannot be undone.`, onConfirm: async ()=>{ setRoster(prev=>prev.filter(c=>c.unit!==co)); setSelected(new Set()); setConfirm(null); await Promise.all(toRemove.filter(c=>c.id).map(c=>fetch(`${API}/roster/${c.id}`,{method:'DELETE'}).catch(e=>console.error('clearCompany:',e)))); } }); };
  const clearAllRoster = () => { const toRemove=[...roster]; setConfirm({ message:`Clear the entire roster (${roster.length} cadets)? This cannot be undone.`, onConfirm: async ()=>{ setRoster([]); setSelected(new Set()); setConfirm(null); await Promise.all(toRemove.filter(c=>c.id).map(c=>fetch(`${API}/roster/${c.id}`,{method:'DELETE'}).catch(e=>console.error('clearAllRoster:',e)))); } }); };

  // ── Edit cadet helpers ──
  const startEdit = (idx, cadet) => {
    setEditingIdx(idx);
    setEditForm({ ...cadet });
  };
  const cancelEdit = () => {
    setEditingIdx(null);
    setEditForm({});
  };
  const saveEdit = async () => {
    if (editingIdx === null) return;
    const updated = [...roster];
    updated[editingIdx] = { ...editForm };
    setRoster(updated);
    setEditingIdx(null);
    setEditForm({});
    if (editForm.id) {
      try {
        await fetch(`${API}/roster/${editForm.id}`, {
          method: 'PUT', headers: {'Content-Type':'application/json'},
          body: JSON.stringify(editForm),
        });
      } catch(e) { console.error('saveEdit:', e); }
    }
  };

  // ── Add new cadet helper ──
  const addNewCadet = async () => {
    if (!newCadetForm.last || !newCadetForm.unit) {
      alert("Last name and Company are required");
      return;
    }
    try {
      const res = await fetch(`${API}/roster`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify(newCadetForm),
      });
      const data = await res.json();
      setRoster(prev => [...prev, { ...newCadetForm, id: data.id }]);
    } catch(e) {
      console.error('addNewCadet:', e);
      setRoster(prev => [...prev, { ...newCadetForm }]);
    }
    setNewCadetForm({
      a:"", username:"", last:"", first:"", mi:"", gen:"", bn:"", unit:"",
      plt:"", sqd:"", rank:"", duty:"", cadetYear:"", email:"", room:"", arrived:"N"
    });
    setShowAddCadet(false);
  };

  // ── Auto-apply approved submissions to roster ──
  const applySubmissionToRoster = async (submission) => {
    if (submission.status !== "APPROVED") return;

    const d = submission.details || {};
    let changes = [];
    const affectedCompanies = new Set();

    // Handle Add/Remove Cadet
    if (submission.type === "Add/Remove Cadet") {
      if (d.action === "ADD") {
        const cadetParts = submission.cadet.split(" ");
        const rankPart = cadetParts[0];
        const namePart = cadetParts.slice(1).join(" ");
        const [last, first] = namePart.split(",").map(s=>s.trim());

        const newCadet = {
          a: "", username: "",
          last: last?.toUpperCase() || "",
          first: first?.replace(".","").toUpperCase() || "",
          gen: "", bn: "",
          unit: d.company || submission.unit,
          plt: "", sqd: "",
          rank: rankPart,
          room: "", arrived: "N"
        };

        try {
          const res = await fetch(`${API}/roster`, {
            method: 'POST', headers: {'Content-Type':'application/json'},
            body: JSON.stringify(newCadet),
          });
          const data = await res.json();
          setRoster(prev => [...prev, { ...newCadet, id: data.id }]);
        } catch(e) {
          console.error('applySubmission ADD:', e);
          setRoster(prev => [...prev, newCadet]);
        }
        changes.push(`Added ${submission.cadet} to roster`);
        affectedCompanies.add(d.company || submission.unit);
      } else if (d.action === "REMOVE") {
        const cadetIdx = roster.findIndex(c =>
          c.last && submission.cadet?.toUpperCase().includes(c.last.toUpperCase())
        );
        if (cadetIdx !== -1) {
          const removedCadet = roster[cadetIdx];
          setRoster(prev => prev.filter((_, i) => i !== cadetIdx));
          changes.push(`Removed ${submission.cadet} from roster`);
          affectedCompanies.add(removedCadet.unit);
          if (removedCadet.id) {
            try { await fetch(`${API}/roster/${removedCadet.id}`, {method:'DELETE'}); } catch(e) { console.error('applySubmission REMOVE:', e); }
          }
        }
      }

      if (changes.length > 0) {
        console.log(`✓ Roster updated: ${changes.join(', ')}`);
        const newNotifications = [];
        affectedCompanies.forEach(company => {
          newNotifications.push({
            id: `${submission.id}_${company}_${Date.now()}`,
            company,
            type: submission.type,
            cadet: submission.cadet,
            details: {
              ...d,
              changes: changes.join(', '),
              isAddition: d.action === "ADD",
              isRemoval: d.action === "REMOVE",
            },
            timestamp: new Date().toISOString(),
          });
        });
        setRosterNotifications(prev => [...prev, ...newNotifications]);
        for (const notif of newNotifications) {
          try {
            await fetch(`${API}/roster-notifications`, {
              method: 'POST', headers: {'Content-Type':'application/json'},
              body: JSON.stringify(notif),
            });
          } catch(e) { console.error('applySubmission notif:', e); }
        }
      }
      return;
    }

    // Find the cadet in the roster by matching last name (for other request types)
    const cadetIdx = roster.findIndex(c =>
      c.last && submission.cadet?.toUpperCase().includes(c.last.toUpperCase())
    );

    if (cadetIdx === -1) return; // Cadet not found in roster

    const updated = [...roster];
    const oldData = { ...updated[cadetIdx] };

    // Apply changes based on submission type
    if (submission.type === "Promotion" && d.promoTo) {
      updated[cadetIdx] = { ...updated[cadetIdx], rank: d.promoTo };
      changes.push(`Rank: ${oldData.rank} → ${d.promoTo}`);
      affectedCompanies.add(oldData.unit);
    }

    if (submission.type === "Room Transfer") {
      if (d.toCo && d.toCo !== oldData.unit) {
        updated[cadetIdx] = { ...updated[cadetIdx], unit: d.toCo };
        changes.push(`Company: ${oldData.unit} → ${d.toCo}`);
        affectedCompanies.add(oldData.unit);
        affectedCompanies.add(d.toCo);
      }
      if (d.toRoom && d.toRoom !== oldData.room) {
        updated[cadetIdx] = { ...updated[cadetIdx], room: d.toRoom };
        changes.push(`Room: ${oldData.room} → ${d.toRoom}`);
        if (!affectedCompanies.has(oldData.unit)) affectedCompanies.add(oldData.unit);
      }
    }

    if (submission.type === "Squad/Platoon Transfer") {
      if (d.toPlt && d.toPlt !== oldData.plt) {
        updated[cadetIdx] = { ...updated[cadetIdx], plt: d.toPlt };
        changes.push(`Platoon: ${oldData.plt || 'None'} → ${d.toPlt}`);
        affectedCompanies.add(oldData.unit);
      }
      if (d.toSqd && d.toSqd !== oldData.sqd) {
        updated[cadetIdx] = { ...updated[cadetIdx], sqd: d.toSqd };
        changes.push(`Squad: ${oldData.sqd || 'None'} → ${d.toSqd}`);
        if (!affectedCompanies.has(oldData.unit)) affectedCompanies.add(oldData.unit);
      }
    }

    if (changes.length > 0) {
      setRoster(updated);
      console.log(`✓ Roster updated for ${submission.cadet}: ${changes.join(', ')}`);

      // Persist roster change
      const updatedEntry = updated[cadetIdx];
      if (updatedEntry.id) {
        try {
          await fetch(`${API}/roster/${updatedEntry.id}`, {
            method: 'PUT', headers: {'Content-Type':'application/json'},
            body: JSON.stringify(updatedEntry),
          });
        } catch(e) { console.error('applySubmission PUT:', e); }
      }

      // Create notifications for affected companies
      const newNotifications = [];
      affectedCompanies.forEach(company => {
        const notifId = `${submission.id}_${company}_${Date.now()}`;
        const isTransferOut = submission.type === "Room Transfer" && d.fromCo === company && d.toCo !== company;
        const isTransferIn = submission.type === "Room Transfer" && d.toCo === company && d.fromCo !== company;

        newNotifications.push({
          id: notifId,
          company,
          type: submission.type,
          cadet: submission.cadet,
          details: {
            ...d,
            changes: changes.join(', '),
            oldRank: oldData.rank,
            oldUnit: oldData.unit,
            oldRoom: oldData.room,
            oldPlt: oldData.plt,
            oldSqd: oldData.sqd,
            newRank: updated[cadetIdx].rank,
            newUnit: updated[cadetIdx].unit,
            newRoom: updated[cadetIdx].room,
            newPlt: updated[cadetIdx].plt,
            newSqd: updated[cadetIdx].sqd,
            isTransferOut,
            isTransferIn,
          },
          timestamp: new Date().toISOString(),
        });
      });
      
      setRosterNotifications(prev => [...prev, ...newNotifications]);
      for (const notif of newNotifications) {
        try {
          await fetch(`${API}/roster-notifications`, {
            method: 'POST', headers: {'Content-Type':'application/json'},
            body: JSON.stringify(notif),
          });
        } catch(e) { console.error('applySubmission notif2:', e); }
      }
    }
  };

  // Helper to handle status changes with auto-apply
  const handleStatusChange = async (submissionId, newStatus) => {
    const sub = submissions.find(s => s.id === submissionId);
    setSubmissions(prev => {
      const updated = prev.map(x => {
        if (x.id === submissionId) {
          const updatedSub = { ...x, status: newStatus };
          if (newStatus === "APPROVED" && x.status !== "APPROVED") {
            setTimeout(() => applySubmissionToRoster(updatedSub), 0);
          }
          return updatedSub;
        }
        return x;
      });
      return updated;
    });
    try {
      await fetch(`${API}/submissions/${submissionId}`, {
        method: 'PUT', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ status: newStatus, s1Note: sub?.s1Note || '' }),
      });
    } catch(e) { console.error('handleStatusChange:', e); }
  };

  const exportOptions = [
    { label:"All Submissions", type:"All" },
    { label:"Promotions",      type:"Promotion" },
    { label:"Awards",          type:"Award" },
    { label:"Room Transfers",  type:"Room Transfer" },
    { label:"Accountability",  type:"Accountability" },
  ];

  const clearedShown  = clearedFilter==="ALL" ? clearedSubmissions : clearedSubmissions.filter(s=>s.type===clearedFilter);
  const clearedTypes  = ["ALL",...Array.from(new Set(clearedSubmissions.map(s=>s.type)))];

  return (
    <div style={{ padding:"32px 36px", maxWidth:1200, margin:"0 auto" }}>
      {showImport && <RosterImportModal onClose={()=>setShowImport(false)} onImport={handleImport} />}
      {selectedCadet && <CadetProfileModal cadet={selectedCadet} submissions={submissions} clearedSubmissions={clearedSubmissions} onClose={()=>setSelectedCadet(null)} />}
      {confirm && <ConfirmDialog message={confirm.message} onConfirm={confirm.onConfirm} onCancel={()=>setConfirm(null)} />}
      {calendarCo && (() => {
        const coRost = roster.filter(r => r.unit === calendarCo);
        const coAcctSubs = [
          ...submissions.filter(s => s.unit===calendarCo && s.type==="Accountability" && (s.details?.formationType||"").toLowerCase().includes("morning")),
          ...clearedSubmissions.filter(s => s.unit===calendarCo && s.type==="Accountability" && (s.details?.formationType||"").toLowerCase().includes("morning")),
        ];
        return <AccountabilityCalendarModal allAcctSubs={coAcctSubs} coRoster={coRost} companyName={calendarCo} onClose={()=>setCalendarCo(null)} />;
      })()}
      
      {/* Add Cadet Modal */}
      {showAddCadet && (
        <div style={{position:"fixed",inset:0,background:"#00000090",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,width:"100%",maxWidth:700,maxHeight:"90vh",overflow:"auto",boxShadow:"0 24px 80px #000000a0"}}>
            <div style={{padding:"20px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontFamily:sans,color:T.accent,fontSize:18,fontWeight:700}}>➕ Add New Cadet to Roster</div>
              <button onClick={()=>setShowAddCadet(false)} style={{background:"transparent",border:"none",color:T.dim,fontSize:22,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:"24px"}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:16,marginBottom:20}}>
                <Field label="A#"><Inp value={newCadetForm.a} onChange={e=>setNewCadetForm({...newCadetForm,a:e.target.value.toUpperCase()})} placeholder="e.g., A123456" /></Field>
                <Field label="Username"><Inp value={newCadetForm.username} onChange={e=>setNewCadetForm({...newCadetForm,username:e.target.value.toUpperCase()})} placeholder="e.g., JSMITH" /></Field>
                <Field label="Last Name" required><Inp value={newCadetForm.last} onChange={e=>setNewCadetForm({...newCadetForm,last:e.target.value.toUpperCase()})} placeholder="SMITH" /></Field>
                <Field label="First Name"><Inp value={newCadetForm.first} onChange={e=>setNewCadetForm({...newCadetForm,first:e.target.value.toUpperCase()})} placeholder="JOHN" /></Field>
                <Field label="MI"><Inp value={newCadetForm.mi} onChange={e=>setNewCadetForm({...newCadetForm,mi:e.target.value.toUpperCase()})} placeholder="e.g., M" style={{maxWidth:80}} /></Field>
                <Field label="Gen"><Inp value={newCadetForm.gen} onChange={e=>setNewCadetForm({...newCadetForm,gen:e.target.value.toUpperCase()})} placeholder="e.g., JR, SR, III" style={{maxWidth:120}} /></Field>
                <Field label="Battalion"><Inp value={newCadetForm.bn} onChange={e=>setNewCadetForm({...newCadetForm,bn:e.target.value.toUpperCase()})} placeholder="e.g., 1" style={{maxWidth:80}} /></Field>
                <Field label="Company" required>
                  <Sel value={newCadetForm.unit} onChange={e=>{
                    const newUnit = e.target.value;
                    setNewCadetForm({
                      ...newCadetForm,
                      unit:newUnit,
                      plt: newUnit==="BATTALION STAFF" ? "" : newCadetForm.plt,
                      sqd: newUnit==="BATTALION STAFF" ? "" : newCadetForm.sqd
                    });
                  }}>
                    <option value="">Select Company</option>
                    {CO_LIST.map(co=><option key={co} value={co}>{co}</option>)}
                  </Sel>
                </Field>
                <Field label="Rank">
                  <Sel value={newCadetForm.rank} onChange={e=>setNewCadetForm({...newCadetForm,rank:e.target.value})}>
                    <option value="">Select Rank</option>
                    {RANKS.map(r=><option key={r} value={r}>{r}</option>)}
                  </Sel>
                </Field>
                {newCadetForm.unit !== "BATTALION STAFF" && (
                  <>
                    <Field label="Platoon"><Inp value={newCadetForm.plt} onChange={e=>setNewCadetForm({...newCadetForm,plt:e.target.value.toUpperCase()})} placeholder="1, 2, or 3" style={{maxWidth:80}} /></Field>
                    <Field label="Squad"><Inp value={newCadetForm.sqd} onChange={e=>setNewCadetForm({...newCadetForm,sqd:e.target.value.toUpperCase()})} placeholder="1, 2, or 3" style={{maxWidth:80}} /></Field>
                  </>
                )}
                <Field label="Duty"><Inp value={newCadetForm.duty} onChange={e=>setNewCadetForm({...newCadetForm,duty:e.target.value})} placeholder="e.g., Squad Leader" /></Field>
                <Field label="Cadet Year"><Inp value={newCadetForm.cadetYear} onChange={e=>setNewCadetForm({...newCadetForm,cadetYear:e.target.value.toUpperCase()})} placeholder="e.g., 3Y1" style={{maxWidth:120}} /></Field>
                <Field label="Email"><Inp type="email" value={newCadetForm.email} onChange={e=>setNewCadetForm({...newCadetForm,email:e.target.value.toLowerCase()})} placeholder="e.g., jsmith@norwich.edu" /></Field>
                <Field label="Room"><Inp value={newCadetForm.room} onChange={e=>setNewCadetForm({...newCadetForm,room:e.target.value})} placeholder="e.g., 204" /></Field>
                <Field label="Arrived">
                  <Sel value={newCadetForm.arrived} onChange={e=>setNewCadetForm({...newCadetForm,arrived:e.target.value})}>
                    <option value="N">No</option>
                    <option value="Y">Yes</option>
                  </Sel>
                </Field>
              </div>
              <div style={{display:"flex",gap:10}}>
                <Btn onClick={addNewCadet} style={{flex:1}}>Add to Roster</Btn>
                <Btn variant="secondary" onClick={()=>setShowAddCadet(false)}>Cancel</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28, flexWrap:"wrap", gap:16 }}>
        <div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:12, letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>S1 Command View · Restricted</div>
          <div style={{ fontFamily:sans, color:T.light, fontSize:26, fontWeight:700 }}>Submission Dashboard</div>
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <Btn variant="green" onClick={()=>setShowImport(true)} style={{ fontSize:12 }}>⬆ Import Roster</Btn>
          <Btn variant="secondary" onClick={onLock} style={{ fontSize:12 }}>🔒 Lock</Btn>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:"flex", gap:14, marginBottom:28, flexWrap:"wrap" }}>
        <Stat label="Total Active"      value={counts.total}              color={T.tan} />
        <Stat label="Pending Action"    value={counts.pending}            color={T.gold} />
        <Stat label="Approved"          value={counts.approved}           color={T.accent} />
        <Stat label="Urgent Flags"      value={counts.urgent}             color={T.red} />
        <Stat label="Cleared Archive"   value={clearedSubmissions.length} color={T.faint} />
        <Stat label="Cadets on Roster"  value={roster.length}             color={T.blue} />
      </div>

      {/* Excel exports */}
      <Card style={{ marginBottom:24 }}>
        {/* Submission reports */}
        <div style={{ fontFamily:sans, color:T.tan, fontSize:13, fontWeight:700, marginBottom:12 }}>⬇ Download Submission Reports</div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
          {exportOptions.map(opt=>(
            <button key={opt.type} onClick={()=>exportSection(opt.type, [...submissions,...clearedSubmissions], roster)}
              style={{ padding:"8px 16px", borderRadius:6, background:T.card2,
                border:`1px solid ${T.border}`, color:T.light, fontFamily:sans,
                fontSize:12, fontWeight:600, cursor:"pointer" }}>
              📊 {opt.label}
            </button>
          ))}
        </div>
        {/* Approved-only note */}
        <div style={{ background:T.gold+"14", border:`1px solid ${T.gold}30`, borderRadius:6,
          padding:"9px 14px", marginBottom:18, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ color:T.gold, fontSize:14 }}>ⓘ</span>
          <span style={{ fontFamily:sans, color:T.gold, fontSize:12 }}>
            Submission reports include all <strong>APPROVED</strong> requests — active and cleared. Set a submission's status to Approved for it to appear in exports.
          </span>
        </div>
        {/* Per-type per-company export buttons */}
        {[
          { label:"Promotions",    type:"Promotion" },
          { label:"Awards",        type:"Award" },
          { label:"Room Transfers",type:"Room Transfer" },
          { label:"Accountability",type:"Accountability" },
        ].map(opt=>(
          <div key={opt.type} style={{ marginBottom:14 }}>
            <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700,
              letterSpacing:0.8, textTransform:"uppercase", marginBottom:8 }}>{opt.label}</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <button onClick={()=>exportSection(opt.type, [...submissions,...clearedSubmissions], roster)}
                style={{ padding:"7px 16px", borderRadius:6, background:T.accent+"20",
                  border:`1px solid ${T.accent}40`, color:T.accent, fontFamily:sans,
                  fontSize:12, fontWeight:700, cursor:"pointer" }}>
                📊 All Companies
              </button>
              {CO_LIST.map(co=>{
                const count = [...submissions,...clearedSubmissions].filter(s=>s.status==="APPROVED"&&s.type===opt.type&&s.unit===co).length;
                return (
                  <button key={co} onClick={()=>exportSection(opt.type, [...submissions,...clearedSubmissions].filter(s=>s.unit===co), roster)}
                    style={{ padding:"7px 14px", borderRadius:6, background:T.card2,
                      border:`1px solid ${T.border}`, fontFamily:sans, fontSize:12,
                      fontWeight:600, cursor:"pointer",
                      color:count>0?T.light:T.faint, opacity:count>0?1:0.55 }}>
                    {co.replace(" CO","")}
                    <span style={{ marginLeft:5, fontFamily:mono, fontSize:10, color:T.dim }}>({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {/* Roster downloads */}
        <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:18, marginTop:4 }}>
          <div style={{ fontFamily:sans, color:T.tan, fontSize:13, fontWeight:700, marginBottom:12 }}>⬇ Download Cadet Rosters</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
            <button onClick={()=>exportSection("Roster", [...submissions,...clearedSubmissions], roster)}
              style={{ padding:"8px 18px", borderRadius:6, background:T.accent+"20",
                border:`1px solid ${T.accent}40`, color:T.accent, fontFamily:sans,
                fontSize:12, fontWeight:700, cursor:"pointer" }}>
              📋 Full Battalion Roster
            </button>
            <div style={{ width:1, height:28, background:T.border, margin:"0 4px" }} />
            {CO_LIST.map(co=>{
              const count = roster.filter(r=>r.unit===co).length;
              return (
                <button key={co} onClick={()=>exportSection(co, [...submissions,...clearedSubmissions], roster)}
                  style={{ padding:"8px 16px", borderRadius:6, background:T.card2,
                    border:`1px solid ${T.border}`, fontFamily:sans, fontSize:12,
                    fontWeight:600, cursor:"pointer",
                    color:count>0?T.light:T.faint, opacity:count>0?1:0.5 }}>
                  📊 {co}
                  <span style={{ marginLeft:6, fontFamily:mono, fontSize:10, color:T.dim }}>({count})</span>
                </button>
              );
            })}
          </div>
          {roster.length===0&&(
            <div style={{ fontFamily:sans, color:T.faint, fontSize:11, marginTop:10 }}>
              No roster loaded — import a roster to enable downloads.
            </div>
          )}
        </div>
        {/* Accountability Master Lists — colored day-by-day Excel + calendar viewer */}
        <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:18, marginTop:4 }}>
          <div style={{ fontFamily:sans, color:T.tan, fontSize:13, fontWeight:700, marginBottom:6 }}>📅 Accountability Master Lists</div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:12, marginBottom:12 }}>
            View day-by-day Morning Formation attendance by date, or download the full color-coded spreadsheet.
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {CO_LIST.map(co => {
              const coRost = roster.filter(r => r.unit === co);
              const coAcctSubs = [
                ...submissions.filter(s => s.unit===co && s.type==="Accountability" && (s.details?.formationType||"").toLowerCase().includes("morning")),
                ...clearedSubmissions.filter(s => s.unit===co && s.type==="Accountability" && (s.details?.formationType||"").toLowerCase().includes("morning")),
              ];
              const hasReports = coAcctSubs.length > 0;
              const hasRoster  = coRost.length > 0;
              return (
                <div key={co} style={{ display:"flex", gap:0, borderRadius:6, overflow:"hidden", border:`1px solid ${T.border}`, opacity: hasReports ? 1 : 0.45 }}>
                  <button
                    onClick={() => setCalendarCo(co)}
                    disabled={!hasReports}
                    title="View attendance by date"
                    style={{
                      padding:"7px 12px", background: hasReports ? T.card2 : "transparent",
                      border:"none", borderRight:`1px solid ${T.border}`, fontFamily:sans, fontSize:12,
                      fontWeight:700, cursor: hasReports ? "pointer" : "not-allowed",
                      color: hasReports ? T.accent : T.faint,
                    }}>
                    📅 {co.replace(" CO","")}
                    <span style={{ marginLeft:5, fontFamily:mono, fontSize:10, color:T.dim }}>
                      ({coAcctSubs.length})
                    </span>
                  </button>
                  <button
                    onClick={() => downloadCompanyAccountabilityMaster(co, coRost, coAcctSubs)}
                    disabled={!hasReports || !hasRoster}
                    title={!hasRoster ? "No roster loaded" : !hasReports ? "No reports" : "Download Excel"}
                    style={{
                      padding:"7px 10px", background: hasReports&&hasRoster ? T.card2 : "transparent",
                      border:"none", fontFamily:sans, fontSize:12,
                      cursor: hasReports&&hasRoster ? "pointer" : "not-allowed",
                      color: hasReports&&hasRoster ? T.dim : T.faint,
                    }}>
                    ⬇
                  </button>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => downloadBattalionAccountabilityWorkbook(roster, submissions, clearedSubmissions)}
            style={{ marginTop:12, padding:"9px 18px", borderRadius:6,
              background:T.olive, border:`1px solid ${T.accent}50`, fontFamily:sans,
              fontSize:12, fontWeight:700, cursor:"pointer", color:T.accent }}>
            ⬇ Download Battalion-Wide Workbook (All Companies + Overview)
          </button>
        </div>
      </Card>

      {/* Dashboard sub-tabs */}
      <div style={{ display:"flex", gap:0, marginBottom:24, borderBottom:`1px solid ${T.border}` }}>
        {[
          ["submissions","📋 Active Submissions", submissions.length, T.blue],
          ["cleared",    "🗂 Cleared History",    clearedSubmissions.length, T.faint],
          ["roster",     "👥 Manage Roster",       roster.length, T.blue],
        ].map(([key,label,count,col])=>(
          <button key={key} onClick={()=>{ setDashTab(key); setSelected(new Set()); }} style={{
            background:"transparent", color:dashTab===key?T.accent:T.dim,
            border:"none", borderBottom:`2px solid ${dashTab===key?T.accent:"transparent"}`,
            fontFamily:sans, fontSize:13, fontWeight:700, padding:"8px 20px 12px",
            cursor:"pointer", transition:"all 0.15s", marginBottom:-1 }}>
            {label}
            {count>0&&<span style={{ marginLeft:8, background:col+"30", color:col, borderRadius:10, padding:"1px 8px", fontSize:11 }}>{count}</span>}
          </button>
        ))}
      </div>

      {/* ── ACTIVE SUBMISSIONS TAB ── */}
      {dashTab==="submissions" && (
        <>
          {/* Auto-update info banner */}
          <div style={{ background:T.accent+"14", border:`1px solid ${T.accent}30`, borderRadius:6,
            padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ color:T.accent, fontSize:14 }}>⚡</span>
            <span style={{ fontFamily:sans, color:T.accent, fontSize:12 }}>
              <strong>Auto-Update Enabled:</strong> When you approve Promotions or Room Transfers, the roster is automatically updated. Cadets transferred to a new company will be removed from their old company's roster.
            </span>
          </div>
          <div style={{ marginBottom:16 }}>
            {/* Type filters */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8, marginBottom:10 }}>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {types.map(t=>(
                  <button key={t} onClick={()=>setFilter(t)} style={{
                    background:filter===t?T.accent:"transparent", color:filter===t?"#0f1410":T.dim,
                    border:`1px solid ${filter===t?T.accent:T.border}`,
                    fontFamily:sans, fontSize:12, fontWeight:600, padding:"6px 14px", borderRadius:20, cursor:"pointer" }}>
                    {t}{t!=="ALL"&&` (${submissions.filter(s=>s.type===t&&(companyFilter==="ALL"||s.unit===companyFilter)).length})`}
                  </button>
                ))}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {selected.size>0&&<button onClick={clearSelected} style={{ padding:"7px 14px", borderRadius:6, background:T.gold+"20", color:T.gold, border:`1px solid ${T.gold}40`, fontFamily:sans, fontSize:12, fontWeight:700, cursor:"pointer" }}>🗂 Clear Selected ({selected.size})</button>}
                {submissions.length>0&&<button onClick={clearAllSubs} style={{ padding:"7px 14px", borderRadius:6, background:"transparent", color:T.dim, border:`1px solid ${T.border}`, fontFamily:sans, fontSize:12, cursor:"pointer" }}>Clear All Active</button>}
              </div>
            </div>
            {/* Company filters */}
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
              <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, marginRight:4 }}>Unit:</span>
              {["ALL",...CO_LIST].map(co=>(
                <button key={co} onClick={()=>setCompanyFilter(co)} style={{
                  background:companyFilter===co?T.blue+"30":"transparent",
                  color:companyFilter===co?T.blue:T.dim,
                  border:`1px solid ${companyFilter===co?T.blue+"60":T.border}`,
                  fontFamily:sans, fontSize:11, fontWeight:600, padding:"4px 12px", borderRadius:14, cursor:"pointer" }}>
                  {co}{co!=="ALL"&&` (${submissions.filter(s=>s.unit===co&&(filter==="ALL"||s.type===filter)).length})`}
                </button>
              ))}
            </div>
          </div>

          <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:"hidden", marginBottom:28 }}>
            <div style={{ display:"grid", gridTemplateColumns:"32px 90px 140px 1fr 130px 110px 100px 200px 44px",
              padding:"10px 18px", background:T.card2,
              fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700, letterSpacing:0.5, textTransform:"uppercase" }}>
              <input type="checkbox" checked={allFilteredSubsSel} onChange={toggleSelectAllSubs}
                style={{ cursor:"pointer", accentColor:T.accent, width:14, height:14 }} />
              <span>Ref ID</span><span>Type</span><span>Cadet</span><span>Unit</span><span>Date</span><span>Priority</span><span>Status</span><span></span>
            </div>
            {filtered.length===0&&(
              <div style={{ padding:40, textAlign:"center" }}>
                <div style={{ fontFamily:sans, color:T.dim, fontSize:14, marginBottom:6 }}>
                  {submissions.length===0
                    ? "No submissions have been received yet."
                    : `No ${filter!=="ALL"?filter+" ":""}submissions${companyFilter!=="ALL"?" for "+companyFilter:""}.`}
                </div>
                {(filter!=="ALL"||companyFilter!=="ALL")&&submissions.length>0&&(
                  <button onClick={()=>{setFilter("ALL");setCompanyFilter("ALL");}}
                    style={{fontFamily:sans,fontSize:12,color:T.accent,background:"transparent",border:`1px solid ${T.accent}40`,borderRadius:4,padding:"4px 12px",cursor:"pointer",marginTop:4}}>
                    Clear filters
                  </button>
                )}
              </div>
            )}
            {filtered.map((s,i)=>{
              const isExp = expandedSub===s.id;
              const d = s.details||{};
              return (
                <div key={s.id} style={{ borderTop:`1px solid ${T.border}`, background:selected.has(s.id)?T.gold+"0e":i%2===0?"transparent":T.card2 }}>
                  {/* Main row */}
                  <div style={{ display:"grid", gridTemplateColumns:"32px 90px 140px 1fr 130px 110px 100px 200px 44px", padding:"13px 18px", alignItems:"center" }}>
                    <input type="checkbox" checked={selected.has(s.id)} onChange={()=>toggleSelectSub(s.id)} style={{ cursor:"pointer", accentColor:T.accent, width:14, height:14 }} />
                    <span style={{ fontFamily:mono, color:T.accent, fontSize:11, cursor:"pointer" }}
                      onClick={()=>setExpandedSub(isExp?null:s.id)}>{s.id}</span>
                    <span style={{ fontFamily:sans, color:T.tan, fontSize:12, fontWeight:600 }}>{s.type}</span>
                    <div style={{ cursor:"pointer" }} onClick={()=>setExpandedSub(isExp?null:s.id)}>
                      <div style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{s.cadet}</div>
                      {s.type==="Award"&&d.awardName&&(
                        <div style={{ fontFamily:sans, color:T.accent, fontSize:11, marginTop:2 }}>🏅 {d.awardName}</div>
                      )}
                      {s.type==="Promotion"&&d.promoFrom&&d.promoTo&&(
                        <div style={{ fontFamily:sans, color:T.blue, fontSize:11, marginTop:2 }}>↑ {d.promoFrom} → {d.promoTo}</div>
                      )}
                      {s.type==="Room Transfer"&&d.fromRoom&&d.toRoom&&(
                        <div style={{ fontFamily:sans, color:T.dim, fontSize:11, marginTop:2 }}>Rm {d.fromRoom} → {d.toRoom}</div>
                      )}
                    </div>
                    <span style={{ fontFamily:sans, color:T.dim, fontSize:12 }}>{s.unit}</span>
                    <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{s.date}</span>
                    <span style={{ fontFamily:sans, fontSize:11, fontWeight:700, color:s.priority==="URGENT"?T.red:s.priority==="PRIORITY"?T.gold:T.faint }}>{s.priority}</span>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <Badge status={s.status} />
                      <select value={s.status} onChange={e=>handleStatusChange(s.id, e.target.value)}
                        style={{ background:T.bg, color:T.dim, border:`1px solid ${T.border}`, fontFamily:sans, fontSize:11, padding:"3px 6px", cursor:"pointer", borderRadius:4 }}>
                        {Object.keys(STATUS_C).map(st=><option key={st}>{st}</option>)}
                      </select>
                    </div>
                    <button onClick={()=>clearOne(s)} title="Clear to history"
                      style={{ background:"transparent", border:`1px solid ${T.border}`, color:T.faint, cursor:"pointer", fontSize:11, fontFamily:sans, padding:"4px 8px", borderRadius:4 }}
                      onMouseEnter={e=>{ e.currentTarget.style.color=T.gold; e.currentTarget.style.borderColor=T.gold+"60"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.color=T.faint; e.currentTarget.style.borderColor=T.border; }}>🗂</button>
                  </div>
                  {/* S1 Note */}
                  <div style={{ padding:"0 18px 10px 60px", display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontFamily:sans, color:T.faint, fontSize:11, whiteSpace:"nowrap" }}>S1 Note:</span>
                    <input value={s.s1Note||""} onChange={e=>setSubmissions(prev=>prev.map(x=>x.id===s.id?{...x,s1Note:e.target.value}:x))}
                      placeholder="Add internal note visible on company status board..."
                      style={{ ...inputBase, fontSize:12, padding:"5px 10px", color:T.dim, background:"transparent", borderColor:"transparent", borderBottom:`1px solid ${T.border}`, borderRadius:0, flex:1 }}
                      onFocus={e=>e.target.style.borderBottomColor=T.accent}
                      onBlur={async e=>{ e.target.style.borderBottomColor=T.border; try{ await fetch(`${API}/submissions/${s.id}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({status:s.status,s1Note:e.target.value})}); }catch(err){ console.error('s1Note save:',err); } }} />
                    <button onClick={()=>setExpandedSub(isExp?null:s.id)}
                      style={{ background:"transparent", border:"none", color:T.dim, cursor:"pointer",
                        fontSize:18, lineHeight:1, padding:"0 4px",
                        transform:isExp?"rotate(90deg)":"none", transition:"transform 0.2s" }}>›</button>
                  </div>
                  {/* Expandable detail panel */}
                  {isExp && (
                    <div style={{ padding:"14px 18px 20px 60px", background:T.bg, borderTop:`1px solid ${T.border}` }}>
                      <div style={{ display:"grid", gridTemplateColumns:"160px 1fr", gap:"8px 20px" }}>
                        <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Submitted By</span>
                        <span style={{ fontFamily:sans, color:T.dim, fontSize:13 }}>{s.unit}</span>
                        {s.type==="Promotion"&&<>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>From Rank</span><span style={{ fontFamily:mono, color:T.tan }}>{d.promoFrom||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>To Rank</span><span style={{ fontFamily:mono, color:T.accent }}>{d.promoTo||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Effective Date</span><span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{d.promoDate||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Justification</span><span style={{ fontFamily:sans, color:T.light, fontSize:13, lineHeight:1.6 }}>{d.promoJust||"—"}</span>
                        </>}
                        {s.type==="Award"&&<>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Award</span><span style={{ fontFamily:sans, color:T.accent, fontSize:13, fontWeight:700 }}>{d.awardName||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Ceremony Date</span><span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{d.awardDate||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Narrative</span><span style={{ fontFamily:sans, color:T.light, fontSize:13, lineHeight:1.7 }}>{d.awardNarrative||"—"}</span>
                        </>}
                        {s.type==="Room Transfer"&&<>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>From</span><span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{d.fromCo||"—"} · Rm {d.fromRoom||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>To</span><span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{d.toCo||"—"} · Rm {d.toRoom||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Effective</span><span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{d.transferDate||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Reason</span><span style={{ fontFamily:sans, color:T.light, fontSize:13, lineHeight:1.6 }}>{d.transferReason||"—"}</span>
                        </>}
                        {s.type==="Accountability"&&<>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Formation</span><span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{d.formationType||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Date / Time</span><span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{d.formationDate||"—"}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>All Accounted</span>
                          <span style={{ fontFamily:sans, fontSize:13, fontWeight:700, color:d.allAccounted?T.accent:T.red }}>{d.allAccounted?"YES":"NO"}</span>
                          {!d.allAccounted&&d.absences?.length>0&&<>
                            <span style={{ fontFamily:sans, color:T.faint, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Unaccounted</span>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                              {d.absences.map((a,ai)=>(
                                <span key={ai} style={{ background:a.status==="UNEXCUSED"?T.red+"20":T.gold+"20",
                                  color:a.status==="UNEXCUSED"?T.red:T.gold,
                                  border:`1px solid ${a.status==="UNEXCUSED"?T.red:T.gold}30`,
                                  borderRadius:4, padding:"3px 10px", fontFamily:sans, fontSize:12 }}>
                                  {a.rank} {a.name} — {a.status}
                                </span>
                              ))}
                            </div>
                          </>}
                        </>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>


        </>
      )}

      {/* ── CLEARED HISTORY TAB ── */}
      {dashTab==="cleared" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {clearedTypes.map(t=>(
                <button key={t} onClick={()=>setClearedFilter(t)} style={{
                  background:clearedFilter===t?T.faint:"transparent", color:clearedFilter===t?T.light:T.dim,
                  border:`1px solid ${clearedFilter===t?T.faint:T.border}`,
                  fontFamily:sans, fontSize:12, fontWeight:600, padding:"5px 14px", borderRadius:20, cursor:"pointer" }}>
                  {t}{t!=="ALL"&&` (${clearedSubmissions.filter(s=>s.type===t).length})`}
                </button>
              ))}
            </div>
            {clearedSubmissions.length>0&&(
              <button onClick={()=>setConfirm({ message:`Permanently delete all ${clearedSubmissions.length} cleared submissions? This cannot be undone.`, onConfirm: async ()=>{ const toDelete=[...clearedSubmissions]; setClearedSubs([]); setConfirm(null); await Promise.all(toDelete.map(s=>fetch(`${API}/cleared-submissions/${s.id}`,{method:'DELETE'}).catch(e=>console.error('purgeCleared:',e)))); } })}
                style={{ padding:"7px 14px", borderRadius:6, background:"transparent", color:T.red, border:`1px solid ${T.red}30`, fontFamily:sans, fontSize:12, cursor:"pointer" }}>
                Purge All Cleared
              </button>
            )}
          </div>

          {clearedShown.length===0 ? (
            <div style={{ border:`2px dashed ${T.border}`, borderRadius:8, padding:"48px 32px", textAlign:"center" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>🗂</div>
              <div style={{ fontFamily:sans, color:T.light, fontSize:16, fontWeight:700, marginBottom:8 }}>No Cleared Submissions</div>
              <div style={{ fontFamily:sans, color:T.dim, fontSize:13 }}>Submissions cleared from Active will appear here.</div>
            </div>
          ) : (
            <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"90px 140px 1fr 130px 110px 140px 120px",
                padding:"10px 18px", background:T.card2,
                fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700, letterSpacing:0.5, textTransform:"uppercase" }}>
                <span>Ref ID</span><span>Type</span><span>Cadet</span><span>Unit</span><span>Submitted</span><span>Cleared</span><span>Final Status</span>
              </div>
              {clearedShown.map((s,i)=>(
                <div key={s.id} style={{ display:"grid", gridTemplateColumns:"90px 140px 1fr 130px 110px 140px 120px",
                  padding:"12px 18px", borderTop:`1px solid ${T.border}`,
                  background:i%2===0?"transparent":T.card2, alignItems:"center" }}>
                  <span style={{ fontFamily:mono, color:T.faint, fontSize:11 }}>{s.id}</span>
                  <span style={{ fontFamily:sans, color:T.dim, fontSize:12 }}>{s.type}</span>
                  <span style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{s.cadet}</span>
                  <span style={{ fontFamily:sans, color:T.dim, fontSize:12 }}>{s.unit}</span>
                  <span style={{ fontFamily:mono, color:T.faint, fontSize:11 }}>{s.date}</span>
                  <span style={{ fontFamily:mono, color:T.faint, fontSize:11 }}>
                    {new Date(s.clearedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                  </span>
                  <Badge status={s.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── MANAGE ROSTER TAB ── */}
      {dashTab==="roster" && (
        <div>
          {roster.length===0 ? (
            <div style={{ border:`2px dashed ${T.border}`, borderRadius:10, padding:"48px 32px", textAlign:"center" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>📋</div>
              <div style={{ fontFamily:sans, color:T.light, fontSize:16, fontWeight:700, marginBottom:8 }}>Roster is Empty</div>
              <div style={{ fontFamily:sans, color:T.dim, fontSize:13 }}>Use the <strong style={{ color:T.accent }}>Import Roster</strong> button above to load cadet data.</div>
            </div>
          ) : (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
                {CO_LIST.map(co=>{
                  const count=roster.filter(c=>c.unit===co).length;
                  return (
                    <div key={co} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"14px 16px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                        <div>
                          <div style={{ fontFamily:sans, color:T.accent, fontSize:13, fontWeight:700 }}>{co}</div>
                          <div style={{ fontFamily:sans, color:T.dim, fontSize:11, marginTop:2 }}>{COMPANIES[co].building} Hall</div>
                        </div>
                        <span style={{ fontFamily:mono, color:T.blue, fontSize:13, fontWeight:700 }}>{count}</span>
                      </div>
                      {count>0&&<button onClick={()=>clearCompany(co)} style={{ width:"100%", marginTop:8, padding:"5px", borderRadius:4, background:"transparent", color:T.red, border:`1px solid ${T.red}30`, fontFamily:sans, fontSize:11, cursor:"pointer", fontWeight:600 }}>Remove All from {co.replace(" CO","")}</button>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
                <Inp value={rosterSearch} onChange={e=>setRosterSearch(e.target.value)} placeholder="Search by name or rank..." style={{ maxWidth:240, fontSize:13 }} />
                <div style={{ display:"flex", gap:6 }}>
                  {["ALL",...CO_LIST].map(c=>(
                    <button key={c} onClick={()=>setRosterCo(c)} style={{ background:rosterCo===c?T.accent:"transparent", color:rosterCo===c?"#0f1410":T.dim, border:`1px solid ${rosterCo===c?T.accent:T.border}`, fontFamily:sans, fontSize:11, fontWeight:600, padding:"5px 12px", borderRadius:14, cursor:"pointer" }}>{c}</button>
                  ))}
                </div>
                <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
                  <button onClick={()=>setShowAddCadet(true)} style={{ padding:"7px 16px", borderRadius:6, background:T.accent+"20", color:T.accent, border:`1px solid ${T.accent}40`, fontFamily:sans, fontSize:12, fontWeight:700, cursor:"pointer" }}>➕ Add Cadet</button>
                  {selected.size>0&&<button onClick={removeSelected} style={{ padding:"7px 16px", borderRadius:6, background:T.red+"20", color:"#d08070", border:`1px solid ${T.red}40`, fontFamily:sans, fontSize:12, fontWeight:700, cursor:"pointer" }}>Remove Selected ({selected.size})</button>}
                  <button onClick={clearAllRoster} style={{ padding:"7px 16px", borderRadius:6, background:"transparent", color:T.red, border:`1px solid ${T.red}30`, fontFamily:sans, fontSize:12, fontWeight:600, cursor:"pointer" }}>Clear Entire Roster</button>
                  <span style={{ fontFamily:sans, color:T.faint, fontSize:12 }}>{filteredRoster.length} cadet{filteredRoster.length!==1?"s":""}</span>
                </div>
              </div>
              <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:"hidden", overflowX:"auto" }}>
                <div style={{ display:"grid", gridTemplateColumns:"32px 70px 110px 100px 90px 50px 50px 50px 120px 50px 50px 70px 110px 70px 160px 70px 100px 68px", minWidth:1420, padding:"10px 16px", background:T.card2, fontFamily:sans, color:T.dim, fontSize:10, fontWeight:700, letterSpacing:0.5, textTransform:"uppercase", alignItems:"center" }}>
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} style={{ cursor:"pointer", accentColor:T.accent, width:14, height:14 }} />
                  <span>A#</span><span>Username</span><span>Last</span><span>First</span><span>MI</span><span>Gen</span><span>BN</span><span>Unit</span><span>Plt</span><span>Sqd</span><span>Rank</span><span>Duty</span><span>Year</span><span>Email</span><span>Room</span><span>Arrived</span><span>Actions</span>
                </div>
                {filteredRoster.length===0&&<div style={{ padding:32, textAlign:"center", fontFamily:sans, color:T.dim }}>No results</div>}
                {filteredRoster.map((c,i)=>{
                  const isEditing = editingIdx === c._idx;
                  return (
                  <div key={c._idx} style={{ display:"grid", gridTemplateColumns:"32px 70px 110px 100px 90px 50px 50px 50px 120px 50px 50px 70px 110px 70px 160px 70px 100px 68px", minWidth:1420, padding:"10px 16px", borderTop:`1px solid ${T.border}`, background:selected.has(c._idx)?T.accent+"12":i%2===0?"transparent":T.card2, alignItems:"center" }}>
                    <input type="checkbox" checked={selected.has(c._idx)} onChange={()=>toggleSelect(c._idx)} style={{ cursor:"pointer", accentColor:T.accent, width:14, height:14 }} disabled={isEditing} />
                    <span style={{ fontFamily:mono, color:T.faint, fontSize:11 }}>{c.a}</span>
                    <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.username}</span>
                    <span style={{ fontFamily:sans, color:T.light, fontSize:12 }}>{c.last}</span>
                    <span style={{ fontFamily:sans, color:T.light, fontSize:12 }}>{c.first}</span>
                    {isEditing ? (
                      <input value={editForm.mi||""} onChange={e=>setEditForm({...editForm,mi:e.target.value.toUpperCase()})} style={{ fontFamily:sans, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4, width:"100%" }} />
                    ) : <span style={{ fontFamily:sans, color:T.dim, fontSize:11 }}>{c.mi}</span>}
                    <span style={{ fontFamily:sans, color:T.dim, fontSize:11 }}>{c.gen}</span>
                    <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.bn}</span>
                    {isEditing ? (
                      <select value={editForm.unit||""} onChange={e=>setEditForm({...editForm,unit:e.target.value})} style={{ fontFamily:sans, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4 }}>
                        {CO_LIST.map(co=><option key={co} value={co}>{co}</option>)}
                      </select>
                    ) : <span style={{ fontFamily:sans, color:T.dim, fontSize:11 }}>{c.unit}</span>}
                    {isEditing ? (
                      <input value={editForm.plt||""} onChange={e=>setEditForm({...editForm,plt:e.target.value.toUpperCase()})} style={{ fontFamily:mono, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4, width:"100%" }} />
                    ) : <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.plt}</span>}
                    {isEditing ? (
                      <input value={editForm.sqd||""} onChange={e=>setEditForm({...editForm,sqd:e.target.value.toUpperCase()})} style={{ fontFamily:mono, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4, width:"100%" }} />
                    ) : <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.sqd}</span>}
                    {isEditing ? (
                      <select value={editForm.rank||""} onChange={e=>setEditForm({...editForm,rank:e.target.value})} style={{ fontFamily:mono, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4 }}>
                        {RANKS.map(r=><option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : <span style={{ fontFamily:mono, color:T.tan, fontSize:12, fontWeight:700 }}>{c.rank}</span>}
                    {isEditing ? (
                      <input value={editForm.duty||""} onChange={e=>setEditForm({...editForm,duty:e.target.value})} style={{ fontFamily:sans, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4, width:"100%" }} />
                    ) : <span style={{ fontFamily:sans, color:T.dim, fontSize:11 }}>{c.duty}</span>}
                    {isEditing ? (
                      <input value={editForm.cadetYear||""} onChange={e=>setEditForm({...editForm,cadetYear:e.target.value.toUpperCase()})} style={{ fontFamily:mono, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4, width:"100%" }} />
                    ) : <span style={{ fontFamily:mono, color:T.dim, fontSize:11 }}>{c.cadetYear}</span>}
                    {isEditing ? (
                      <input type="email" value={editForm.email||""} onChange={e=>setEditForm({...editForm,email:e.target.value.toLowerCase()})} style={{ fontFamily:sans, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4, width:"100%" }} />
                    ) : <span style={{ fontFamily:sans, color:T.faint, fontSize:11 }}>{c.email}</span>}
                    {isEditing ? (
                      <input value={editForm.room||""} onChange={e=>setEditForm({...editForm,room:e.target.value})} style={{ fontFamily:mono, fontSize:11, padding:"4px", background:T.card, color:T.light, border:`1px solid ${T.accent}`, borderRadius:4, width:"100%" }} />
                    ) : <span style={{ fontFamily:mono, color:T.accent, fontSize:12 }}>{c.room}</span>}
                    <span style={{ fontFamily:sans, color:T.faint, fontSize:11 }}>{c.arrived}</span>
                    {isEditing ? (
                      <div style={{ display:"flex", gap:4 }}>
                        <button onClick={saveEdit} title="Save changes" style={{ background:T.accent, border:"none", color:"#0f1410", cursor:"pointer", fontSize:11, lineHeight:1, padding:"4px 8px", borderRadius:4, fontWeight:700 }}>✓</button>
                        <button onClick={cancelEdit} title="Cancel" style={{ background:"transparent", border:`1px solid ${T.border}`, color:T.dim, cursor:"pointer", fontSize:11, lineHeight:1, padding:"4px 8px", borderRadius:4 }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display:"flex", gap:4 }}>
                        <button onClick={()=>startEdit(c._idx, c)} title="Edit cadet" style={{ background:"transparent", border:"none", color:T.faint, cursor:"pointer", fontSize:14, lineHeight:1, padding:2, borderRadius:4 }} onMouseEnter={e=>e.target.style.color=T.accent} onMouseLeave={e=>e.target.style.color=T.faint}>✏</button>
                        <button onClick={()=>removeSingle(c)} title="Remove cadet" style={{ background:"transparent", border:"none", color:T.faint, cursor:"pointer", fontSize:16, lineHeight:1, padding:2, borderRadius:4 }} onMouseEnter={e=>e.target.style.color=T.red} onMouseLeave={e=>e.target.style.color=T.faint}>✕</button>
                      </div>
                    )}
                  </div>
                )})}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  SUBMIT FORM
// ─────────────────────────────────────────────
function SubmitForm({ submissions, setSubmissions, roster }) {
  const [category, setCategory]     = useState("");
  const [priority, setPriority]     = useState("ROUTINE");
  const [submitted, setSubmitted]   = useState(false);
  const [refId, setRefId]           = useState("");

  const [subRank, setSubRank] = useState("");
  const [subName, setSubName] = useState("");
  const [unit, setUnit]       = useState("");
  const [savedSubmitter, setSavedSubmitter] = useState({ rank:"", name:"", unit:"" });

  const [rank, setRank]           = useState("");
  const [lastName, setLastName]   = useState("");
  const [firstName, setFirstName] = useState("");

  const [promoFrom, setPromoFrom] = useState("");
  const [promoTo, setPromoTo]     = useState("");
  const [promoDate, setPromoDate] = useState("");
  const [promoJust, setPromoJust] = useState("");

  const [awardName, setAwardName]       = useState("");
  const [awardDate, setAwardDate]       = useState("");
  const [awardNarrative, setAwardNarrative] = useState("");

  const [fromCo, setFromCo]               = useState("");
  const [fromRoom, setFromRoom]           = useState("");
  const [toCo, setToCo]                   = useState("");
  const [toRoom, setToRoom]               = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferDate, setTransferDate]   = useState("");

  const [acctType, setAcctType]       = useState("");
  const [acctDate, setAcctDate]       = useState("");
  const [allAccounted, setAllAccounted] = useState(true);
  const [absences, setAbsences]       = useState([{ rank:"", name:"", status:"UNEXCUSED", reason:"" }]);

  // Squad/Platoon Transfer
  const [fromPlt, setFromPlt]         = useState("");
  const [fromSqd, setFromSqd]         = useState("");
  const [toPlt, setToPlt]             = useState("");
  const [toSqd, setToSqd]             = useState("");
  const [sqpltReason, setSqpltReason] = useState("");
  const [sqpltDate, setSqpltDate]     = useState("");

  // Add/Remove Cadet
  const [addRemoveAction, setAddRemoveAction] = useState(""); // "ADD" or "REMOVE"
  const [addRemoveReason, setAddRemoveReason] = useState("");

  const addAbsence = () => setAbsences(p=>[...p,{ rank:"",name:"",status:"UNEXCUSED",reason:"" }]);
  const updAbsence = (i,f,v) => setAbsences(p=>p.map((a,idx)=>idx===i?{...a,[f]:v}:a));
  const remAbsence = (i) => setAbsences(p=>p.filter((_,idx)=>idx!==i));

  // ── Autocomplete / pre-fill from roster ──
  const [lastSuggestions, setLastSuggestions] = useState([]);
  const [showLastSug, setShowLastSug]         = useState(false);
  const [subSuggestions, setSubSuggestions]   = useState([]);
  const [showSubSug, setShowSubSug]           = useState(false);

  const handleLastNameChange = (val) => {
    setLastName(val.toUpperCase());
    if (val.length >= 2) {
      const matches = roster.filter(r =>
        r.last?.toUpperCase().startsWith(val.toUpperCase())
      ).slice(0, 8);
      setLastSuggestions(matches);
      setShowLastSug(matches.length > 0);
    } else {
      setLastSuggestions([]); setShowLastSug(false);
    }
  };

  const selectCadetFromRoster = (cadet) => {
    setLastName(cadet.last||"");
    setFirstName(cadet.first||"");
    const rawRank = (cadet.rank||"").trim().toUpperCase();
    setRank(RANKS.includes(rawRank) ? rawRank : "");
    if (category === "ROOM_TRANSFER") {
      setFromCo(cadet.unit||"");
      setFromRoom(cadet.room||"");
    }
    setShowLastSug(false);
    setLastSuggestions([]);
  };

  const handleSubNameChange = (val) => {
    setSubName(val);
    if (val.length >= 2) {
      const q = val.toUpperCase();
      const matches = roster.filter(r =>
        r.last?.toUpperCase().startsWith(q) ||
        `${r.last} ${r.first}`.toUpperCase().includes(q) ||
        r.username?.toUpperCase().startsWith(q)
      ).slice(0, 8);
      setSubSuggestions(matches);
      setShowSubSug(matches.length > 0);
    } else {
      setSubSuggestions([]); setShowSubSug(false);
    }
  };

  const selectSubmitterFromRoster = (cadet) => {
    const name = `${cadet.last}, ${cadet.first}`;
    // Normalize rank to match RANKS array (roster may have lowercase/extra spaces)
    const rawRank = (cadet.rank||"").trim().toUpperCase();
    const rank = RANKS.includes(rawRank) ? rawRank : "";
    const coUnit = cadet.unit||"";
    setSubName(name);
    setSubRank(rank);
    setUnit(coUnit);
    setSavedSubmitter({ rank, name, unit:coUnit });
    setShowSubSug(false);
    setSubSuggestions([]);
  };

  const reset = (keepSubmitter=false) => {
    setCategory(""); setRank("");
    setLastName(""); setFirstName(""); setPromoFrom(""); setPromoTo(""); setPromoDate(""); setPromoJust("");
    setAwardName(""); setAwardDate(""); setAwardNarrative("");
    setFromCo(""); setFromRoom(""); setToCo(""); setToRoom(""); setTransferReason(""); setTransferDate("");
    setAcctType(""); setAcctDate(""); setAllAccounted(true);
    setAbsences([{ rank:"",name:"",status:"UNEXCUSED",reason:"" }]);
    if (keepSubmitter && savedSubmitter.name) {
      setSubRank(savedSubmitter.rank);
      setSubName(savedSubmitter.name);
      setUnit(savedSubmitter.unit);
    } else {
      setSubRank(""); setSubName(""); setUnit("");
    }
  };

  const handleSubmit = async () => {
    if (!category || !unit || !subName) return;
    if (category === "ACCOUNTABILITY" && (!acctType || !acctDate)) return;
    const id = `S1-${Date.now().toString(36).slice(-6).toUpperCase()}`;
    const typeMap = {
      PROMOTION:"Promotion",
      AWARD:"Award",
      ROOM_TRANSFER:"Room Transfer",
      SQUAD_PLATOON_TRANSFER:"Squad/Platoon Transfer",
      ADD_REMOVE_CADET:"Add/Remove Cadet",
      ACCOUNTABILITY:"Accountability"
    };
    const cadetStr = category==="ACCOUNTABILITY"
      ? `${subRank} ${subName}`
      : `${rank} ${lastName.toUpperCase()}, ${firstName.charAt(0).toUpperCase()}.`;
    const details = category==="ACCOUNTABILITY"
      ? { formationType:acctType, formationDate:acctDate, allAccounted, absences:allAccounted?[]:absences.filter(a=>a.name) }
      : category==="PROMOTION"
      ? { promoFrom, promoTo, promoDate, promoJust }
      : category==="AWARD"
      ? { awardName, awardDate, awardNarrative }
      : category==="ROOM_TRANSFER"
      ? { fromCo, fromRoom, toCo, toRoom, transferDate, transferReason }
      : category==="SQUAD_PLATOON_TRANSFER"
      ? { fromPlt, fromSqd, toPlt, toSqd, sqpltDate, sqpltReason }
      : category==="ADD_REMOVE_CADET"
      ? { action:addRemoveAction, reason:addRemoveReason, company:unit }
      : {};
    const newSub = { id, type:typeMap[category], cadet:cadetStr, unit, date:new Date().toISOString().split("T")[0], status:"PENDING", priority, details };
    setSubmissions(p=>[newSub,...p]);
    setRefId(id); setSubmitted(true);
    try {
      await fetch(`${API}/submissions`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify(newSub),
      });
    } catch(e) { console.error('handleSubmit:', e); }
  };

  if (submitted) {
    return (
      <div style={{ padding:32, maxWidth:520, margin:"80px auto", textAlign:"center" }}>
        <div style={{ width:64, height:64, borderRadius:"50%", background:T.accent+"25", border:`2px solid ${T.accent}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          margin:"0 auto 20px", fontSize:26, color:T.accent }}>✓</div>
        <div style={{ fontFamily:sans, color:T.light, fontSize:22, fontWeight:700, marginBottom:8 }}>Submission Received</div>
        <div style={{ fontFamily:sans, color:T.dim, fontSize:13, marginBottom:24 }}>
          Your report has been logged and forwarded to S1 staff for review.
        </div>
        <Card style={{ marginBottom:28 }}>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:11, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Reference ID</div>
          <div style={{ fontFamily:mono, color:T.accent, fontSize:22, fontWeight:700 }}>{refId}</div>
          <div style={{ fontFamily:sans, color:T.faint, fontSize:11, marginTop:6 }}>Retain this ID for follow-up with S1</div>
        </Card>
        <Btn onClick={()=>{ setSubmitted(false); reset(true); }}>Submit Another</Btn>
      </div>
    );
  }

  const g2 = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 };
  const g3 = { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 };

  return (
    <div style={{ padding:"32px 36px", maxWidth:900, margin:"0 auto" }}>
      <div style={{ marginBottom:28 }}>
        <div style={{ fontFamily:sans, color:T.dim, fontSize:12, letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>1st Battalion S-1</div>
        <div style={{ fontFamily:sans, color:T.light, fontSize:26, fontWeight:700 }}>Submit Request to S1</div>
        <div style={{ fontFamily:sans, color:T.dim, fontSize:13, marginTop:6, maxWidth:620, lineHeight:1.7 }}>
          Use this form to submit personnel actions to S1. All cadets have a personal responsibility to keep their chain of command informed of their location and duty status.
        </div>
      </div>

      <Card>
        <SecHead n="1" label="Submitting Party" />
        <div style={g3}>
          <Field label="Rank" required>
            <Sel value={subRank} onChange={e=>setSubRank(e.target.value)}>
              <option value="">Select</option>
              {RANKS.map(r=><option key={r}>{r}</option>)}
            </Sel>
          </Field>
          <Field label="Name (Last, First)" required>
            <div style={{ position:"relative" }}>
              <Inp value={subName} onChange={e=>handleSubNameChange(e.target.value)}
                onBlur={()=>setTimeout(()=>setShowSubSug(false),150)}
                placeholder="SMITH, JOHN" />
              {showSubSug && (
                <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:50,
                  background:T.card, border:`1px solid ${T.accent}`, borderRadius:"0 0 6px 6px",
                  boxShadow:"0 8px 24px #00000060", maxHeight:200, overflow:"auto" }}>
                  {subSuggestions.map((c,i)=>(
                    <div key={i} onMouseDown={()=>selectSubmitterFromRoster(c)}
                      style={{ padding:"8px 14px", cursor:"pointer", fontFamily:sans, fontSize:13,
                        borderBottom:`1px solid ${T.border}`, display:"flex", gap:10, alignItems:"center" }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.accent+"20"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <span style={{ fontFamily:mono, color:T.tan, fontSize:11, minWidth:40 }}>{c.rank}</span>
                      <span style={{ color:T.light }}>{c.last}, {c.first}</span>
                      <span style={{ color:T.dim, fontSize:11, marginLeft:"auto" }}>{c.unit}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label="Company" required>
            <Sel value={unit} onChange={e=>setUnit(e.target.value)}>
              <option value="">Select Company</option>
              {CO_LIST.map(c=><option key={c}>{c}</option>)}
            </Sel>
          </Field>
        </div>
      </Card>

      <Card>
        <SecHead n="2" label="Request Type" />
        <div style={g2}>
          <Field label="Category" required>
            <Sel value={category} onChange={e=>setCategory(e.target.value)}>
              <option value="">Select Category</option>
              <option value="PROMOTION">Promotion</option>
              <option value="AWARD">Award Submission</option>
              <option value="ROOM_TRANSFER">Room Assignment / Transfer</option>
              <option value="SQUAD_PLATOON_TRANSFER">Squad / Platoon Transfer</option>
              <option value="ADD_REMOVE_CADET">Add / Remove Cadet from Roster</option>
              <option value="ACCOUNTABILITY">Formation Accountability</option>
            </Sel>
          </Field>
          {category && category!=="ACCOUNTABILITY" && (
            <Field label="Priority">
              <Sel value={priority} onChange={e=>setPriority(e.target.value)}>
                <option>ROUTINE</option><option>PRIORITY</option><option>URGENT</option>
              </Sel>
            </Field>
          )}
        </div>
      </Card>

      {category && category!=="ACCOUNTABILITY" && (
        <Card>
          <SecHead n="3" label="Cadet Information" />
          <div style={g3}>
            <Field label="Rank" required>
              <Sel value={rank} onChange={e=>setRank(e.target.value)}>
                <option value="">Select</option>
                {RANKS.map(r=><option key={r}>{r}</option>)}
              </Sel>
            </Field>
            <Field label="Last Name" required>
              <div style={{ position:"relative" }}>
                <Inp value={lastName} onChange={e=>handleLastNameChange(e.target.value)}
                  onBlur={()=>setTimeout(()=>setShowLastSug(false),150)}
                  placeholder={roster.length>0?"Start typing to search roster...":"Last name"} />
                {showLastSug && (
                  <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:50,
                    background:T.card, border:`1px solid ${T.accent}`, borderRadius:"0 0 6px 6px",
                    boxShadow:"0 8px 24px #00000060", maxHeight:220, overflow:"auto" }}>
                    {lastSuggestions.map((c,i)=>(
                      <div key={i} onMouseDown={()=>selectCadetFromRoster(c)}
                        style={{ padding:"9px 14px", cursor:"pointer", fontFamily:sans, fontSize:13,
                          borderBottom:`1px solid ${T.border}`, display:"flex", gap:10, alignItems:"center" }}
                        onMouseEnter={e=>e.currentTarget.style.background=T.accent+"20"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{ fontFamily:mono, color:T.tan, fontSize:11, minWidth:40 }}>{c.rank}</span>
                        <span style={{ color:T.light, fontWeight:600 }}>{c.last},</span>
                        <span style={{ color:T.light }}>{c.first}</span>
                        <span style={{ color:T.dim, fontSize:11 }}>{c.plt ? `Plt ${c.plt}` : ""}</span>
                        <span style={{ color:T.dim, fontSize:11, marginLeft:"auto" }}>{c.unit} · Rm {c.room}</span>
                      </div>
                    ))}
                    {roster.length>0&&<div style={{ padding:"6px 14px", fontFamily:sans, color:T.faint, fontSize:10 }}>
                      Select to auto-fill rank, name & room info
                    </div>}
                  </div>
                )}
              </div>
            </Field>
            <Field label="First Name">
              <Inp value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder="First name" />
            </Field>
          </div>
        </Card>
      )}

      {category==="PROMOTION" && (
        <Card>
          <SecHead n="4" label="Promotion Details" />
          <div style={g3}>
            <Field label="From Rank">
              <Sel value={promoFrom} onChange={e=>setPromoFrom(e.target.value)}>
                <option value="">Select</option>{RANKS.map(r=><option key={r}>{r}</option>)}
              </Sel>
            </Field>
            <Field label="To Rank">
              <Sel value={promoTo} onChange={e=>setPromoTo(e.target.value)}>
                <option value="">Select</option>{RANKS.map(r=><option key={r}>{r}</option>)}
              </Sel>
            </Field>
            <Field label="Effective Date">
              <Inp type="date" value={promoDate} onChange={e=>setPromoDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Justification / Remarks">
            <Ta value={promoJust} onChange={e=>setPromoJust(e.target.value)} placeholder="Basis for promotion..." />
          </Field>
        </Card>
      )}

      {category==="AWARD" && (
        <Card>
          <SecHead n="4" label="Award Details" />
          <div style={g2}>
            <Field label="Award" required>
              <Sel value={awardName} onChange={e=>setAwardName(e.target.value)}>
                <option value="">Select Award</option>
                {AWARDS.map(a=><option key={a}>{a}</option>)}
              </Sel>
            </Field>
            <Field label="Date of Award / Ceremony">
              <Inp type="date" value={awardDate} onChange={e=>setAwardDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Narrative / Justification" required>
            <Ta style={{ minHeight:110 }} value={awardNarrative} onChange={e=>setAwardNarrative(e.target.value)}
              placeholder="Describe the actions or accomplishments warranting this award..." />
          </Field>
        </Card>
      )}

      {category==="ROOM_TRANSFER" && (
        <Card>
          <SecHead n="4" label="Room Assignment / Transfer" />
          <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:12 }}>Current Assignment</div>
          <div style={{ ...g2, marginBottom:20 }}>
            <Field label="Current Company">
              <Sel value={fromCo} onChange={e=>setFromCo(e.target.value)}>
                <option value="">Select</option>
                {CO_LIST.map(c=><option key={c} value={c}>{c} — {COMPANIES[c].building}</option>)}
              </Sel>
            </Field>
            <Field label={fromCo ? `Current Room — ${COMPANIES[fromCo]?.building}` : "Current Room"}>
              <Inp value={fromRoom} onChange={e=>setFromRoom(e.target.value)}
                placeholder={fromCo ? `e.g. 204` : "Select company first"} disabled={!fromCo} />
            </Field>
          </div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:12 }}>New Assignment</div>
          <div style={g3}>
            <Field label="New Company">
              <Sel value={toCo} onChange={e=>setToCo(e.target.value)}>
                <option value="">Select</option>
                {CO_LIST.map(c=><option key={c} value={c}>{c} — {COMPANIES[c].building}</option>)}
              </Sel>
            </Field>
            <Field label={toCo ? `New Room — ${COMPANIES[toCo]?.building}` : "New Room"}>
              <Inp value={toRoom} onChange={e=>setToRoom(e.target.value)}
                placeholder={toCo ? `e.g. 112` : "Select company first"} disabled={!toCo} />
            </Field>
            <Field label="Effective Date">
              <Inp type="date" value={transferDate} onChange={e=>setTransferDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Reason">
            <Ta value={transferReason} onChange={e=>setTransferReason(e.target.value)} placeholder="Reason for room change..." />
          </Field>
        </Card>
      )}

      {category==="SQUAD_PLATOON_TRANSFER" && (
        <Card>
          <SecHead n="4" label="Squad / Platoon Transfer" />
          <div style={{ background:T.blue+"14", border:`1px solid ${T.blue}30`, borderRadius:6, padding:"12px 14px", marginBottom:16 }}>
            <div style={{ fontFamily:sans, color:T.blue, fontSize:12, lineHeight:1.6 }}>
              <strong>Note:</strong> Battalion Staff does not have platoons or squads. This request type is only for line company cadets (Alpha, Bravo, Charlie, Delta).
            </div>
          </div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:12 }}>Current Assignment</div>
          <div style={{ ...g2, marginBottom:20 }}>
            <Field label="Current Platoon">
              <Sel value={fromPlt} onChange={e=>setFromPlt(e.target.value)}>
                <option value="">Select</option>
                <option value="1">1st Platoon</option>
                <option value="2">2nd Platoon</option>
                <option value="3">3rd Platoon</option>
              </Sel>
            </Field>
            <Field label="Current Squad">
              <Sel value={fromSqd} onChange={e=>setFromSqd(e.target.value)}>
                <option value="">Select</option>
                <option value="1">1st Squad</option>
                <option value="2">2nd Squad</option>
                <option value="3">3rd Squad</option>
              </Sel>
            </Field>
          </div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:12 }}>New Assignment</div>
          <div style={g3}>
            <Field label="New Platoon">
              <Sel value={toPlt} onChange={e=>setToPlt(e.target.value)}>
                <option value="">Select</option>
                <option value="1">1st Platoon</option>
                <option value="2">2nd Platoon</option>
                <option value="3">3rd Platoon</option>
              </Sel>
            </Field>
            <Field label="New Squad">
              <Sel value={toSqd} onChange={e=>setToSqd(e.target.value)}>
                <option value="">Select</option>
                <option value="1">1st Squad</option>
                <option value="2">2nd Squad</option>
                <option value="3">3rd Squad</option>
              </Sel>
            </Field>
            <Field label="Effective Date">
              <Inp type="date" value={sqpltDate} onChange={e=>setSqpltDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Reason">
            <Ta value={sqpltReason} onChange={e=>setSqpltReason(e.target.value)} placeholder="Reason for squad/platoon reassignment..." />
          </Field>
        </Card>
      )}

      {category==="ADD_REMOVE_CADET" && (
        <Card>
          <SecHead n="4" label="Add / Remove Cadet from Roster" />
          <div style={{ marginBottom:20 }}>
            <Field label="Action" required>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={()=>setAddRemoveAction("ADD")} style={{
                  flex:1, padding:"14px", borderRadius:8, cursor:"pointer", fontFamily:sans,
                  fontSize:13, fontWeight:700, transition:"all 0.15s",
                  border:`2px solid ${addRemoveAction==="ADD"?T.accent:T.border}`,
                  background:addRemoveAction==="ADD"?T.accent+"20":"transparent",
                  color:addRemoveAction==="ADD"?T.accent:T.dim }}>
                  ➕ Add to Roster
                </button>
                <button onClick={()=>setAddRemoveAction("REMOVE")} style={{
                  flex:1, padding:"14px", borderRadius:8, cursor:"pointer", fontFamily:sans,
                  fontSize:13, fontWeight:700, transition:"all 0.15s",
                  border:`2px solid ${addRemoveAction==="REMOVE"?T.red:T.border}`,
                  background:addRemoveAction==="REMOVE"?T.red+"20":"transparent",
                  color:addRemoveAction==="REMOVE"?T.red:T.dim }}>
                  ➖ Remove from Roster
                </button>
              </div>
            </Field>
          </div>
          <Field label="Reason" required>
            <Ta value={addRemoveReason} onChange={e=>setAddRemoveReason(e.target.value)} 
              placeholder={addRemoveAction==="ADD" ? "Reason for adding cadet (e.g., new enrollment, transfer in)..." : "Reason for removing cadet (e.g., transfer out, graduation, dismissal)..."} />
          </Field>
          {addRemoveAction==="REMOVE" && (
            <div style={{ background:T.red+"14", border:`1px solid ${T.red}30`, borderRadius:6, padding:"12px 14px", marginTop:12 }}>
              <div style={{ fontFamily:sans, color:T.red, fontSize:12, lineHeight:1.6 }}>
                <strong>Warning:</strong> Removing a cadet from the roster will permanently delete their record from all company rosters. This action should only be used for permanent separations (graduation, dismissal, permanent transfers).
              </div>
            </div>
          )}
        </Card>
      )}

      {category==="ACCOUNTABILITY" && (
        <Card>
          <SecHead n="3" label="Formation Accountability Report" />
          <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:6,
            padding:"14px 18px", marginBottom:20 }}>
            <div style={{ fontFamily:sans, color:T.dim, fontSize:12, lineHeight:1.8 }}>
              <strong style={{ color:T.tan }}>Default assumption: All cadets are accounted for.</strong>{" "}
              Only report if cadets were absent. All absences must be documented per S1 accountability procedures.
              Each cadet has a personal responsibility to keep their chain of command informed of their location and duty status.
            </div>
          </div>

          <div style={g2}>
            <Field label="Formation Type" required>
              <Sel value={acctType} onChange={e=>setAcctType(e.target.value)}>
                <option value="">Select</option>
                {FORMATION_TYPES.map(f=><option key={f}>{f}</option>)}
              </Sel>
            </Field>
            <Field label="Date & Time" required>
              <Inp type="datetime-local" value={acctDate} onChange={e=>setAcctDate(e.target.value)} />
            </Field>
          </div>

          <div style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:20, marginBottom:20 }}>
            <div style={{ fontFamily:sans, color:T.light, fontSize:14, fontWeight:600, marginBottom:14 }}>Accountability Status</div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setAllAccounted(true)} style={{
                flex:1, padding:"14px", borderRadius:8, cursor:"pointer", fontFamily:sans,
                fontSize:13, fontWeight:700, transition:"all 0.15s",
                border:`2px solid ${allAccounted?T.accent:T.border}`,
                background:allAccounted?T.accent+"20":"transparent",
                color:allAccounted?T.accent:T.dim }}>
                ✓ All Accounted For
              </button>
              <button onClick={()=>setAllAccounted(false)} style={{
                flex:1, padding:"14px", borderRadius:8, cursor:"pointer", fontFamily:sans,
                fontSize:13, fontWeight:700, transition:"all 0.15s",
                border:`2px solid ${!allAccounted?T.red:T.border}`,
                background:!allAccounted?T.red+"20":"transparent",
                color:!allAccounted?T.red:T.dim }}>
                ⚠ Report Unaccounted ({!allAccounted ? absences.filter(a=>a.name).length||"—" : 0})
              </button>
            </div>
          </div>

          {!allAccounted && (
            <div>
              <div style={{ fontFamily:sans, color:T.tan, fontSize:13, fontWeight:700, marginBottom:12 }}>
                Unaccounted Cadets
              </div>
              {absences.map((a,i)=>(
                <div key={i} style={{ background:T.card2, border:`1px solid ${T.border}`, borderRadius:6,
                  padding:16, marginBottom:10, position:"relative" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"130px 1fr 170px 1fr", gap:12 }}>
                    <Field label="Rank">
                      <Sel value={a.rank} onChange={e=>updAbsence(i,"rank",e.target.value)}>
                        <option value="">—</option>
                        {RANKS.map(r=><option key={r}>{r}</option>)}
                      </Sel>
                    </Field>
                    <Field label="Name (Last, First)" required>
                      <div style={{ position:"relative" }}>
                        <Inp value={a.name} onChange={e=>{
                          updAbsence(i,"name",e.target.value);
                        }}
                        onInput={e=>{
                          const val = e.target.value;
                          if(val.length>=2&&roster.length>0){
                            const q=val.toUpperCase();
                            const m=roster.filter(r=>r.last?.toUpperCase().startsWith(q)||`${r.last} ${r.first}`.toUpperCase().includes(q)).slice(0,8);
                            e.target._absMatches=m;
                            e.target._absIdx=i;
                            // Force re-render via a synthetic event trick — use dataset
                            e.target.dataset.sug = m.length>0?"1":"0";
                          } else {
                            e.target.dataset.sug="0";
                          }
                        }}
                        placeholder="DOE, JANE" />
                        {/* Absence autocomplete using local state per-row */}
                        <AbsenceAutoComplete
                          value={a.name} roster={roster} idx={i}
                          onSelect={(cadet,idx)=>{
                            updAbsence(idx,"name",`${cadet.last}, ${cadet.first}`);
                            updAbsence(idx,"rank",(()=>{const r=(cadet.rank||"").trim().toUpperCase();return RANKS.includes(r)?r:""})());
                          }}
                        />
                      </div>
                    </Field>
                    <Field label="Absence Type">
                      <Sel value={a.status} onChange={e=>updAbsence(i,"status",e.target.value)}>
                        {ABSENCE_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                      </Sel>
                    </Field>
                    <Field label="Reason / Notes">
                      <Inp value={a.reason} onChange={e=>updAbsence(i,"reason",e.target.value)}
                        placeholder={a.status!=="UNEXCUSED"?"Required — reason/authorization...":"Optional..."} />
                    </Field>
                  </div>
                  {absences.length>1 && (
                    <button onClick={()=>remAbsence(i)} style={{ position:"absolute", top:10, right:12,
                      background:"transparent", color:T.red, border:"none", cursor:"pointer", fontSize:16 }}>✕</button>
                  )}
                </div>
              ))}
              <button onClick={addAbsence} style={{ width:"100%", padding:"10px", borderRadius:6,
                background:"transparent", color:T.accent, border:`1px dashed ${T.accent}50`,
                fontFamily:sans, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                + Add Another Cadet
              </button>
            </div>
          )}
        </Card>
      )}

      {category && (() => {
        const missingAcct = category === "ACCOUNTABILITY" && (!acctType || !acctDate);
        return (
          <div style={{ display:"flex", gap:12, alignItems:"center", marginTop:8 }}>
            <Btn onClick={handleSubmit} disabled={missingAcct} style={{ fontSize:14, padding:"13px 40px" }}>Submit to S1 →</Btn>
            <span style={{ fontFamily:sans, color: missingAcct ? T.gold : T.faint, fontSize:12 }}>
              {missingAcct ? "Formation Type and Date & Time are required before submitting." : "Will be forwarded to S1 for processing."}
            </span>
          </div>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────
//  CADET PROFILE MODAL
// ─────────────────────────────────────────────
function CadetProfileModal({ cadet, submissions, clearedSubmissions, onClose }) {
  const allSubs = [...submissions, ...clearedSubmissions];
  const matchSub = (s) => s.cadet?.toUpperCase().includes(cadet.last?.toUpperCase());
  const promos   = allSubs.filter(s=>s.type==="Promotion"&&matchSub(s));
  const awards   = allSubs.filter(s=>s.type==="Award"&&matchSub(s));
  const transfers= allSubs.filter(s=>s.type==="Room Transfer"&&matchSub(s));
  const accts    = allSubs.filter(s=>s.type==="Accountability"&&(
    s.unit===cadet.unit && (s.details?.allAccounted===false
      ? (s.details?.absences||[]).some(a=>(a.name||"").toUpperCase().includes(cadet.last?.toUpperCase()))
      : true)
  ));

  const Section = ({title, color, items, renderItem}) => items.length===0 ? null : (
    <div style={{marginBottom:20}}>
      <div style={{fontFamily:sans,color,fontSize:12,fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:10,borderBottom:`1px solid ${T.border}`,paddingBottom:6}}>{title} ({items.length})</div>
      {items.map((s,i)=>(
        <div key={i} style={{padding:"10px 14px",background:T.card2,borderRadius:6,marginBottom:6,border:`1px solid ${T.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,flexWrap:"wrap",gap:6}}>
            <span style={{fontFamily:mono,color:T.accent,fontSize:11}}>{s.id}</span>
            <span style={{fontFamily:mono,color:T.faint,fontSize:11}}>{s.date}</span>
            <Badge status={s.status}/>
          </div>
          {renderItem(s)}
          {s.s1Note&&<div style={{marginTop:6,padding:"6px 10px",background:T.card,border:`1px solid ${T.accent}30`,borderLeft:`3px solid ${T.accent}`,borderRadius:3,fontFamily:sans,color:T.light,fontSize:12}}>{s.s1Note}</div>}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"#00000090",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,width:"100%",maxWidth:700,maxHeight:"90vh",overflow:"auto",boxShadow:"0 24px 80px #000000a0"}}>
        {/* Header */}
        <div style={{padding:"20px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:4}}>
              <span style={{fontFamily:mono,color:T.tan,fontSize:16,fontWeight:700}}>{cadet.rank}</span>
              <span style={{fontFamily:sans,color:T.light,fontSize:20,fontWeight:700}}>{cadet.last}, {cadet.first}</span>
            </div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              <span style={{fontFamily:sans,color:T.dim,fontSize:12}}>{cadet.unit}</span>
              {cadet.duty&&<span style={{fontFamily:sans,color:T.dim,fontSize:12}}>{cadet.duty}</span>}
              {cadet.cadetYear&&<span style={{fontFamily:sans,color:T.dim,fontSize:12}}>{cadet.cadetYear}</span>}
              {cadet.room&&<span style={{fontFamily:sans,color:T.dim,fontSize:12}}>Room {cadet.room}</span>}
              {cadet.plt&&<span style={{fontFamily:sans,color:T.dim,fontSize:12}}>Plt {cadet.plt}</span>}
              {cadet.sqd&&<span style={{fontFamily:sans,color:T.dim,fontSize:12}}>Sqd {cadet.sqd}</span>}
              {cadet.email&&<span style={{fontFamily:mono,color:T.dim,fontSize:11}}>{cadet.email}</span>}
              {cadet.a&&<span style={{fontFamily:mono,color:T.faint,fontSize:11}}>A# {cadet.a}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{background:"transparent",border:"none",color:T.dim,fontSize:22,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{padding:"20px 24px"}}>
          {promos.length===0&&awards.length===0&&transfers.length===0&&accts.length===0&&(
            <div style={{textAlign:"center",padding:"32px 0"}}><div style={{fontFamily:sans,color:T.dim,fontSize:14,marginBottom:4}}>No records found for {cadet.rank} {cadet.last}.</div><div style={{fontFamily:sans,color:T.faint,fontSize:12}}>Promotions, awards, transfers, and accountability reports will appear here once submitted and approved.</div></div>
          )}
          <Section title="Promotions" color={T.blue} items={promos} renderItem={s=>(
            <div style={{fontFamily:sans,color:T.dim,fontSize:12}}>
              <span style={{color:T.tan}}>{s.details?.promoFrom}</span> → <span style={{color:T.accent,fontWeight:700}}>{s.details?.promoTo}</span>
              {s.details?.promoDate&&<span style={{marginLeft:10}}>Effective: {s.details.promoDate}</span>}
              {s.details?.promoJust&&<div style={{marginTop:4,color:T.light,fontSize:12}}>{s.details.promoJust}</div>}
            </div>
          )}/>
          <Section title="Awards" color={T.accent} items={awards} renderItem={s=>(
            <div>
              <div style={{fontFamily:sans,color:T.accent,fontSize:13,fontWeight:700}}>🏅 {s.details?.awardName}</div>
              {s.details?.awardDate&&<div style={{fontFamily:sans,color:T.dim,fontSize:12,marginTop:2}}>Ceremony: {s.details.awardDate}</div>}
              {s.details?.awardNarrative&&<div style={{fontFamily:sans,color:T.light,fontSize:12,marginTop:4,lineHeight:1.6}}>{s.details.awardNarrative}</div>}
            </div>
          )}/>
          <Section title="Room Transfers" color={T.gold} items={transfers} renderItem={s=>(
            <div style={{fontFamily:sans,color:T.dim,fontSize:12}}>
              <span>{s.details?.fromCo} Rm {s.details?.fromRoom}</span> → <span style={{color:T.light}}>{s.details?.toCo} Rm {s.details?.toRoom}</span>
              {s.details?.transferDate&&<span style={{marginLeft:10}}>Effective: {s.details.transferDate}</span>}
              {s.details?.transferReason&&<div style={{marginTop:4,color:T.light}}>{s.details.transferReason}</div>}
            </div>
          )}/>
          <Section title="Accountability Reports" color={T.dim} items={accts} renderItem={s=>{
            const abs=(s.details?.absences||[]).find(a=>(a.name||"").toUpperCase().includes(cadet.last?.toUpperCase()));
            return (
              <div style={{fontFamily:sans,fontSize:12}}>
                <span style={{color:T.dim}}>{s.details?.formationType}</span>
                {s.details?.formationDate&&<span style={{color:T.faint,marginLeft:8}}>{s.details.formationDate}</span>}
                {abs
                  ? <span style={{marginLeft:8,color:abs.status==="EXCUSED"?"#e0a8f8":T.red,fontWeight:700}}>{abs.status}{abs.reason?" — "+abs.reason:""}</span>
                  : <span style={{marginLeft:8,color:T.accent,fontWeight:700}}>✓ PRESENT</span>}
              </div>
            );
          }}/>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPANY STATUS BOARD
// ─────────────────────────────────────────────
function CompanyStatusBoard({ submissions, clearedSubmissions, roster, rosterNotifications, setRosterNotifications }) {
  const [selectedCo, setSelectedCo] = useState("");
  const [pw, setPw]                 = useState("");
  const [unlocked, setUnlocked]     = useState("");
  const [err, setErr]               = useState(false);
  const [shake, setShake]           = useState(false);
  const [loading, setLoading]       = useState(false);
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [boardTab, setBoardTab]     = useState("active"); // "active" | "cleared" | "roster"
  const [rosterSearch, setRosterSearch] = useState("");
  const [expanded, setExpanded]     = useState(null);
  const [selectedBoardCadet, setSelectedBoardCadet] = useState(null);
  const [viewingNotif, setViewingNotif]   = useState(null);
  const [acctCalendar, setAcctCalendar]   = useState(false);

  const attempt = async () => {
    if (!selectedCo || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/company`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ company: selectedCo, password: pw }),
      });
      if (res.ok) { setUnlocked(selectedCo); setErr(false); setPw(""); setLoading(false); return; }
    } catch(e) { console.error(e); }
    setLoading(false);
    setErr(true); setShake(true); setPw("");
    setTimeout(() => setShake(false), 500);
  };

  const switchCompany = () => { setUnlocked(""); setSelectedCo(""); setTypeFilter("ALL"); setBoardTab("active"); setRosterSearch(""); };

  // Get notifications for this company
  const coNotifications = rosterNotifications.filter(n => n.company === unlocked);
  
  const dismissNotification = async (notifId) => {
    setRosterNotifications(prev => prev.filter(n => n.id !== notifId));
    setViewingNotif(null);
    try {
      await fetch(`${API}/roster-notifications/${notifId}`, { method: 'DELETE' });
    } catch(e) { console.error('dismissNotification:', e); }
  };

  const dismissAllNotifications = async () => {
    const toDelete = rosterNotifications.filter(n => n.company === unlocked);
    setRosterNotifications(prev => prev.filter(n => n.company !== unlocked));
    await Promise.all(toDelete.map(n =>
      fetch(`${API}/roster-notifications/${n.id}`, { method: 'DELETE' }).catch(e => console.error('dismissAll:', e))
    ));
  };

  // Active submissions for this company
  const coSubs = submissions.filter(s => s.unit === unlocked);

  // Cleared submissions for this company:
  // — Under 24h since clearing: shown on the ACTIVE board (still visible, recently cleared)
  // — 24h+ since clearing: moved to the CLEARED tab
  const now = Date.now();
  const coClearedRecent = clearedSubmissions.filter(s =>
    s.unit === unlocked && (now - new Date(s.clearedAt).getTime()) < 24 * 60 * 60 * 1000
  );
  const coClearedVisible = clearedSubmissions.filter(s =>
    s.unit === unlocked && (now - new Date(s.clearedAt).getTime()) >= 24 * 60 * 60 * 1000
  );
  // Combined active list: true actives + recently cleared (< 24h)
  const coActiveAll = [...coSubs, ...coClearedRecent];

  const activeTypes  = ["ALL", ...Array.from(new Set(coActiveAll.map(s=>s.type)))];
  const clearedTypes = ["ALL", ...Array.from(new Set(coClearedVisible.map(s=>s.type)))];
  const shownActive  = typeFilter==="ALL" ? coActiveAll : coActiveAll.filter(s=>s.type===typeFilter);
  const shownCleared = typeFilter==="ALL" ? coClearedVisible : coClearedVisible.filter(s=>s.type===typeFilter);
  const currentTypes = boardTab==="active" ? activeTypes : clearedTypes;

  // Excel export for company — official roster format, cadet columns first
  const exportCompanyType = async (type) => {
    const approved = [...coActiveAll, ...coClearedVisible].filter(s=>s.status==="APPROVED");
    const typeSubs = type==="All" ? approved : approved.filter(s=>s.type===type);
    const wb = new ExcelJS.Workbook();
    const date = new Date().toISOString().split("T")[0];
    const headers = (extra) => [...ROSTER_HEADER_LABELS, "Ref ID", "Date Submitted", "S1 Note", ...extra];
    const widths  = (extra) => [...ROSTER_HEADER_WIDTHS, 10, 14, 30, ...extra];
    const row = (s, extra) => {
      const base = submissionRosterRow(s, roster, extra);
      delete base["Priority"];
      return base;
    };

    if (type==="All") {
      const rows = typeSubs.map(s=>row(s, { "Type":s.type }));
      addFormattedSheet(wb, "All Approved", headers(["Type"]), widths([10]), rows);
    } else if (type==="Promotion") {
      const rows = typeSubs.map(s=>row(s, {"From Rank":s.details?.promoFrom||"","To Rank":s.details?.promoTo||"","Effective Date":s.details?.promoDate||"","Justification":s.details?.promoJust||""}));
      addFormattedSheet(wb, "Promotions", headers(["From Rank","To Rank","Effective Date","Justification"]), widths([10,10,14,40]), rows);
    } else if (type==="Award") {
      const rows = typeSubs.map(s=>row(s, {"Award":s.details?.awardName||"","Ceremony":s.details?.awardDate||"","Narrative":s.details?.awardNarrative||""}));
      addFormattedSheet(wb, "Awards", headers(["Award","Ceremony","Narrative"]), widths([28,14,50]), rows);
    } else if (type==="Room Transfer") {
      const rows = typeSubs.map(s=>row(s, {"From Co":s.details?.fromCo||"","From Rm":s.details?.fromRoom||"","To Co":s.details?.toCo||"","To Rm":s.details?.toRoom||"","Effective":s.details?.transferDate||"","Reason":s.details?.transferReason||""}));
      addFormattedSheet(wb, "Room Transfers", headers(["From Co","From Rm","To Co","To Rm","Effective","Reason"]), widths([12,8,12,8,14,30]), rows);
    } else if (type==="Accountability") {
      const rows = typeSubs.map(s=>row(s, {"Formation":s.details?.formationType||"","Date/Time":s.details?.formationDate||"","All Accounted":s.details?.allAccounted?"YES":"NO","# Unaccounted":s.details?.absences?.length||0}));
      addFormattedSheet(wb, "Accountability", headers(["Formation","Date/Time","All Accounted","# Unaccounted"]), widths([18,18,14,14]), rows);
    }
    await downloadWorkbook(wb, `${unlocked.replace(" ","_")}_${type.replace(" ","_")}_${date}.xlsx`);
  };

  // Legacy full download (kept for all-in-one)
  const exportCompany = () => exportCompanyType("All");

  // Detail panel (inline)
  const DetailPanel = ({ s }) => {
    const d = s.details || {};
    const rows = [];
    if (s.type==="Promotion") {
      if (d.promoFrom) rows.push(["From Rank",d.promoFrom]);
      if (d.promoTo)   rows.push(["To Rank",d.promoTo]);
      if (d.promoDate) rows.push(["Effective Date",d.promoDate]);
      if (d.promoJust) rows.push(["Justification",d.promoJust]);
    }
    if (s.type==="Award") {
      if (d.awardName)      rows.push(["Award",d.awardName]);
      if (d.awardDate)      rows.push(["Ceremony",d.awardDate]);
      if (d.awardNarrative) rows.push(["Narrative",d.awardNarrative]);
    }
    if (s.type==="Room Transfer") {
      if (d.fromCo)         rows.push(["From Company",d.fromCo]);
      if (d.fromRoom)       rows.push(["From Room",d.fromRoom]);
      if (d.toCo)           rows.push(["To Company",d.toCo]);
      if (d.toRoom)         rows.push(["To Room",d.toRoom]);
      if (d.transferDate)   rows.push(["Effective Date",d.transferDate]);
      if (d.transferReason) rows.push(["Reason",d.transferReason]);
    }
    if (s.type==="Accountability") {
      rows.push(["Formation Type",d.formationType||"—"]);
      rows.push(["Date / Time",d.formationDate||"—"]);
      rows.push(["All Accounted",d.allAccounted?"Yes":"No"]);
      if (!d.allAccounted&&d.absences?.length)
        rows.push(["Unaccounted",d.absences.map(a=>`${a.rank} ${a.name} (${a.status}${a.reason?` — ${a.reason}`:""})`).join(", ")]);
    }
    return (
      <div style={{ padding:"14px 20px 18px", background:T.bg, borderTop:`1px solid ${T.border}` }}>
        <div style={{ display:"grid", gridTemplateColumns:"160px 1fr", gap:"6px 16px", marginBottom:s.s1Note?14:0 }}>
          {rows.map(([k,v],i)=>(
            <React.Fragment key={i}>
              <span style={{ fontFamily:sans, color:T.dim, fontSize:12, fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}>{k}</span>
              <span style={{ fontFamily:sans, color:T.light, fontSize:13, lineHeight:1.6 }}>{v}</span>
            </React.Fragment>
          ))}
        </div>
        {s.s1Note&&(
          <div style={{ marginTop:14, padding:"10px 14px", background:T.card, border:`1px solid ${T.accent}30`, borderLeft:`3px solid ${T.accent}`, borderRadius:4 }}>
            <div style={{ fontFamily:sans, color:T.accent, fontSize:10, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>S1 Note</div>
            <div style={{ fontFamily:sans, color:T.light, fontSize:13, lineHeight:1.6 }}>{s.s1Note}</div>
          </div>
        )}
      </div>
    );
  };

  // ── Gate ──
  if (!unlocked) {
    return (
      <div style={{ padding:"32px 36px", maxWidth:900, margin:"0 auto" }}>
        <div style={{ marginBottom:28 }}>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:12, letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>1st Battalion S-1</div>
          <div style={{ fontFamily:sans, color:T.light, fontSize:26, fontWeight:700 }}>Company Status Board</div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:13, marginTop:6, lineHeight:1.7, maxWidth:560 }}>
            View the full status of submissions for your company. Select your company and enter your unit access code.
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14, marginBottom:28, maxWidth:600 }}>
          {CO_LIST.map(co=>(
            <button key={co} onClick={()=>{ setSelectedCo(co); setErr(false); setPw(""); }}
              style={{ padding:"18px 20px", borderRadius:8, cursor:"pointer", textAlign:"left",
                border:`2px solid ${selectedCo===co?T.accent:T.border}`,
                background:selectedCo===co?T.accent+"18":T.card, transition:"all 0.15s" }}>
              <div style={{ fontFamily:sans, color:selectedCo===co?T.accent:T.light, fontSize:14, fontWeight:700 }}>{co}</div>
              <div style={{ fontFamily:sans, color:T.dim, fontSize:12, marginTop:3 }}>{COMPANIES[co].building} Hall</div>
            </button>
          ))}
        </div>
        {selectedCo&&(
          <div style={{ maxWidth:420 }}>
            <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-10px)}60%{transform:translateX(10px)}}`}</style>
            <div style={{ animation:shake?"shake 0.4s":"none" }}>
              <Field label={`${selectedCo} Access Code`} required>
                <Inp type="password" value={pw} onChange={e=>{ setPw(e.target.value); setErr(false); }}
                  onKeyDown={e=>e.key==="Enter"&&attempt()} placeholder="Enter unit code" />
              </Field>
              {err&&<div style={{ fontFamily:sans, color:T.red, fontSize:12, marginBottom:12 }}>⚠ Invalid access code</div>}
              <Btn onClick={attempt} disabled={!pw||loading} style={{ width:"100%", padding:"12px", fontSize:14 }}>
                {loading ? "Verifying…" : `View ${selectedCo} Status Board →`}
              </Btn>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Board ──
  const statusCounts = Object.fromEntries(Object.keys(STATUS_C).map(st=>[st,coActiveAll.filter(s=>s.status===st).length]));
  const shownList    = boardTab==="active" ? shownActive : shownCleared;

  return (
    <div style={{ padding:"32px 36px", maxWidth:1100, margin:"0 auto" }}>
      {selectedBoardCadet && <CadetProfileModal cadet={selectedBoardCadet} submissions={submissions} clearedSubmissions={clearedSubmissions} onClose={()=>setSelectedBoardCadet(null)} />}
      {acctCalendar && unlocked && (() => {
        const coRoster = roster.filter(r => r.unit === unlocked);
        const coAcctSubs = [
          ...submissions.filter(s => s.unit===unlocked && s.type==="Accountability" && (s.details?.formationType||"").toLowerCase().includes("morning")),
          ...clearedSubmissions.filter(s => s.unit===unlocked && s.type==="Accountability" && (s.details?.formationType||"").toLowerCase().includes("morning")),
        ];
        return <AccountabilityCalendarModal allAcctSubs={coAcctSubs} coRoster={coRoster} companyName={unlocked} onClose={()=>setAcctCalendar(false)} />;
      })()}
      {viewingNotif && (
        <div style={{position:"fixed",inset:0,background:"#00000090",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,width:"100%",maxWidth:600,boxShadow:"0 24px 80px #000000a0"}}>
            <div style={{padding:"20px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontFamily:sans,color:T.accent,fontSize:18,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
                <span>🔔</span> Roster Update
              </div>
              <button onClick={()=>setViewingNotif(null)} style={{background:"transparent",border:"none",color:T.dim,fontSize:22,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:"24px"}}>
              <div style={{marginBottom:20}}>
                <div style={{fontFamily:sans,color:T.dim,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Cadet</div>
                <div style={{fontFamily:sans,color:T.light,fontSize:18,fontWeight:700}}>{viewingNotif.cadet}</div>
              </div>
              <div style={{marginBottom:20}}>
                <div style={{fontFamily:sans,color:T.dim,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Change Type</div>
                <div style={{fontFamily:sans,color:T.accent,fontSize:14,fontWeight:600}}>{viewingNotif.type}</div>
              </div>
              <div style={{marginBottom:20}}>
                <div style={{fontFamily:sans,color:T.dim,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Details</div>
                <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,padding:"14px 16px"}}>
                  {viewingNotif.details.isTransferOut && (
                    <div style={{fontFamily:sans,color:T.red,fontSize:13,marginBottom:10,fontWeight:700}}>⬅ TRANSFERRED OUT</div>
                  )}
                  {viewingNotif.details.isTransferIn && (
                    <div style={{fontFamily:sans,color:T.accent,fontSize:13,marginBottom:10,fontWeight:700}}>➡ TRANSFERRED IN</div>
                  )}
                  <div style={{display:"grid",gridTemplateColumns:"120px 1fr",gap:"8px 16px",fontFamily:sans,fontSize:13}}>
                    {viewingNotif.type === "Promotion" && (
                      <>
                        <span style={{color:T.faint}}>Old Rank:</span>
                        <span style={{color:T.tan,fontWeight:600}}>{viewingNotif.details.oldRank}</span>
                        <span style={{color:T.faint}}>New Rank:</span>
                        <span style={{color:T.accent,fontWeight:700}}>{viewingNotif.details.newRank}</span>
                      </>
                    )}
                    {viewingNotif.type === "Room Transfer" && (
                      <>
                        <span style={{color:T.faint}}>From:</span>
                        <span style={{color:T.light}}>{viewingNotif.details.oldUnit} · Room {viewingNotif.details.oldRoom}</span>
                        <span style={{color:T.faint}}>To:</span>
                        <span style={{color:T.accent,fontWeight:700}}>{viewingNotif.details.newUnit} · Room {viewingNotif.details.newRoom}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div style={{marginBottom:20}}>
                <div style={{fontFamily:sans,color:T.dim,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Timestamp</div>
                <div style={{fontFamily:mono,color:T.faint,fontSize:12}}>{new Date(viewingNotif.timestamp).toLocaleString()}</div>
              </div>
              <div style={{background:T.accent+"14",border:`1px solid ${T.accent}30`,borderRadius:6,padding:"12px 14px",marginBottom:20}}>
                <div style={{fontFamily:sans,color:T.accent,fontSize:12,lineHeight:1.6}}>
                  <strong>Action:</strong> {viewingNotif.details.isTransferOut 
                    ? `${viewingNotif.cadet} has been removed from ${unlocked}'s roster and is now assigned to ${viewingNotif.details.newUnit}.`
                    : viewingNotif.details.isTransferIn
                    ? `${viewingNotif.cadet} has been added to ${unlocked}'s roster from ${viewingNotif.details.oldUnit}.`
                    : `${viewingNotif.cadet}'s roster information has been updated.`}
                </div>
              </div>
              <button onClick={()=>dismissNotification(viewingNotif.id)} style={{
                width:"100%",padding:"12px",borderRadius:6,background:T.accent,
                color:"#0f1410",border:"none",fontFamily:sans,fontSize:14,
                fontWeight:700,cursor:"pointer"}}>
                Acknowledge & Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:12, letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>Company Status Board</div>
          <div style={{ fontFamily:sans, color:T.light, fontSize:26, fontWeight:700 }}>{unlocked}</div>
          <div style={{ fontFamily:sans, color:T.dim, fontSize:13, marginTop:4 }}>
            {COMPANIES[unlocked].building} Hall · {coSubs.length} active · {coClearedRecent.length > 0 && <span style={{ color:T.gold, marginLeft:4 }}>+{coClearedRecent.length} recently cleared (visible for 24h)</span>} · {coClearedVisible.length} archived
          </div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          {[
            {label:"All",          type:"All"},
            {label:"Promotions",   type:"Promotion"},
            {label:"Awards",       type:"Award"},
            {label:"Transfers",    type:"Room Transfer"},
            {label:"Accountability",type:"Accountability"},
          ].map(opt=>{
            const count = [...coActiveAll,...coClearedVisible].filter(s=>s.status==="APPROVED"&&(opt.type==="All"||s.type===opt.type)).length;
            return (
              <button key={opt.type} onClick={()=>exportCompanyType(opt.type)}
                style={{ padding:"7px 13px", borderRadius:6,
                  background:count>0?T.card2:"transparent",
                  border:`1px solid ${count>0?T.border:T.faint+"40"}`,
                  color:count>0?T.light:T.faint, fontFamily:sans, fontSize:11,
                  fontWeight:600, cursor:count>0?"pointer":"default", opacity:count>0?1:0.5 }}>
                📊 {opt.label}
                {count>0&&<span style={{ marginLeft:4,fontFamily:mono,fontSize:10,color:T.dim }}>({count})</span>}
              </button>
            );
          })}
          <div style={{ width:1, height:24, background:T.border }} />
          <Btn variant="secondary" onClick={switchCompany} style={{ fontSize:12 }}>← Switch Company</Btn>
        </div>
      </div>

      {/* Status tiles (active only) */}
      <div style={{ display:"flex", gap:12, marginBottom:24, flexWrap:"wrap" }}>
        {Object.entries(STATUS_C).map(([st,col])=>(
          <div key={st} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"14px 20px", flex:1, minWidth:100, borderTop:`3px solid ${col}` }}>
            <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:600, marginBottom:4 }}>{st}</div>
            <div style={{ fontFamily:mono, color:col, fontSize:26, fontWeight:700 }}>{statusCounts[st]||0}</div>
          </div>
        ))}
      </div>

      {/* Board sub-tabs */}
      {(() => {
        const coRoster = roster.filter(c => c.unit === unlocked);
        const filteredCoRoster = coRoster.filter(c =>
          !rosterSearch || `${c.rank} ${c.last} ${c.first} ${c.username}`.toLowerCase().includes(rosterSearch.toLowerCase())
        );
        const arrivedCount   = coRoster.filter(c => (c.arrived||"").toUpperCase() === "Y").length;
        const unarrivedCount = coRoster.filter(c => (c.arrived||"").toUpperCase() !== "Y").length;

        return (
          <>
            <div style={{ display:"flex", gap:0, marginBottom:20, borderBottom:`1px solid ${T.border}`, flexWrap:"wrap" }}>
              {[
                ["active",  "📋 Active Submissions", coActiveAll.length,    T.blue],
                ["cleared", "🗂 Cleared Archive",     coClearedVisible.length, T.faint],
                ["roster",  "👥 Company Roster",      coRoster.length,       T.accent],
                ["acctmaster","📅 Accountability Master","",T.gold],
              ].map(([key,label,count,col])=>(
                <button key={key} onClick={()=>{ setBoardTab(key); setTypeFilter("ALL"); setExpanded(null); setRosterSearch(""); }} style={{
                  background:"transparent", color:boardTab===key?T.accent:T.dim,
                  border:"none", borderBottom:`2px solid ${boardTab===key?T.accent:"transparent"}`,
                  fontFamily:sans, fontSize:13, fontWeight:700, padding:"8px 20px 12px",
                  cursor:"pointer", marginBottom:-1 }}>
                  {label}
                  {count>0&&<span style={{ marginLeft:8, background:col+"30", color:col, borderRadius:10, padding:"1px 8px", fontSize:11 }}>{count}</span>}
                </button>
              ))}
            </div>

            {/* ── SUBMISSIONS TABS (active / cleared) ── */}
            {boardTab !== "roster" && (
              <>
                <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
                  {currentTypes.map(t=>(
                    <button key={t} onClick={()=>setTypeFilter(t)} style={{
                      background:typeFilter===t?T.accent:"transparent", color:typeFilter===t?"#0f1410":T.dim,
                      border:`1px solid ${typeFilter===t?T.accent:T.border}`,
                      fontFamily:sans, fontSize:12, fontWeight:600, padding:"5px 14px", borderRadius:16, cursor:"pointer" }}>
                      {t}{t!=="ALL"&&` (${(boardTab==="active"?coActiveAll:coClearedVisible).filter(s=>s.type===t).length})`}
                    </button>
                  ))}
                </div>

                {shownList.length===0 ? (
                  <div style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:48, textAlign:"center", fontFamily:sans, color:T.dim, fontSize:13 }}>
                    {boardTab==="cleared"
                      ? coClearedRecent.length>0
                        ? `${coClearedRecent.length} submission${coClearedRecent.length>1?"s":""} will move here from Active after 24 hours.`
                        : "No archived submissions yet."
                      : typeFilter!=="ALL"
                        ? `No ${typeFilter.toLowerCase()} submissions for ${unlocked}.`
                        : `${unlocked} has no active submissions yet.`}
                  </div>
                ) : (
                  <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:"hidden" }}>
                    <div style={{ display:"grid", gridTemplateColumns:"100px 140px 1fr 120px 32px",
                      padding:"10px 18px", background:T.card2,
                      fontFamily:sans, color:T.dim, fontSize:11, fontWeight:700, letterSpacing:0.5, textTransform:"uppercase" }}>
                      <span>Ref ID</span><span>Type</span><span>Cadet</span><span>Status</span><span></span>
                    </div>
                    {shownList.map((s,i)=>(
                      <div key={s.id} style={{ borderTop:`1px solid ${T.border}`, background:i%2===0?"transparent":T.card2 }}>
                        <div style={{ display:"grid", gridTemplateColumns:"100px 140px 1fr 120px 32px",
                          padding:"13px 18px", alignItems:"center", cursor:"pointer" }}
                          onClick={()=>setExpanded(expanded===s.id?null:s.id)}>
                          <span style={{ fontFamily:mono, color:boardTab==="cleared"?T.faint:s.clearedAt?T.faint:T.accent, fontSize:11 }}>
                            {s.id}{s.clearedAt&&boardTab==="active"&&<span style={{ marginLeft:5, color:T.faint, fontFamily:sans, fontSize:9, fontWeight:700, letterSpacing:0.5 }}>CLEARED</span>}
                          </span>
                          <span style={{ fontFamily:sans, color:T.tan, fontSize:12, fontWeight:600 }}>{s.type}</span>
                          <div>
                            <div style={{ fontFamily:sans, color:T.light, fontSize:13 }}>{s.cadet}</div>
                            <div style={{ fontFamily:mono, color:T.faint, fontSize:11, marginTop:2 }}>
                              {s.date}
                              {boardTab==="cleared"&&s.clearedAt&&<span style={{ marginLeft:8, color:T.faint }}>· cleared {new Date(s.clearedAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span>}
                            </div>
                          </div>
                          <Badge status={s.status} />
                          <span style={{ fontFamily:sans, color:T.dim, fontSize:14, textAlign:"center",
                            transform:expanded===s.id?"rotate(90deg)":"none", transition:"transform 0.2s", display:"inline-block" }}>›</span>
                        </div>
                        {expanded===s.id&&<DetailPanel s={s} />}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── COMPANY ROSTER TAB ── */}
            {boardTab === "roster" && (
              <div>
                {/* Roster Update Notifications */}
                {coNotifications.length > 0 && (
                  <div style={{ marginBottom:20 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <div style={{ fontFamily:sans, color:T.accent, fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:18 }}>🔔</span>
                        Roster Updates ({coNotifications.length})
                      </div>
                      {coNotifications.length > 1 && (
                        <button onClick={dismissAllNotifications} style={{
                          padding:"6px 12px", borderRadius:6, background:"transparent",
                          border:`1px solid ${T.border}`, color:T.dim, fontFamily:sans,
                          fontSize:11, cursor:"pointer", fontWeight:600 }}>
                          Dismiss All
                        </button>
                      )}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {coNotifications.map(notif => {
                        const d = notif.details;
                        return (
                          <div key={notif.id} style={{
                            background:T.card, border:`1px solid ${T.accent}40`,
                            borderLeft:`4px solid ${T.accent}`, borderRadius:6,
                            padding:"12px 16px", display:"flex", justifyContent:"space-between",
                            alignItems:"flex-start", gap:12 }}>
                            <div style={{ flex:1 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                                <span style={{ fontFamily:sans, color:T.accent, fontSize:13, fontWeight:700 }}>
                                  {d.isTransferOut ? "⬅ Cadet Transferred Out" : d.isTransferIn ? "➡ Cadet Transferred In" : "Roster Updated"}
                                </span>
                                <span style={{ fontFamily:mono, color:T.faint, fontSize:10 }}>
                                  {new Date(notif.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <div style={{ fontFamily:sans, color:T.light, fontSize:13, marginBottom:4 }}>
                                <strong>{notif.cadet}</strong>
                              </div>
                              <div style={{ fontFamily:sans, color:T.dim, fontSize:12 }}>
                                {d.isTransferOut && `Transferred from ${d.oldUnit} Rm ${d.oldRoom} → ${d.newUnit} Rm ${d.newRoom}`}
                                {d.isTransferIn && `Transferred from ${d.oldUnit} Rm ${d.oldRoom} → ${d.newUnit} Rm ${d.newRoom}`}
                                {!d.isTransferOut && !d.isTransferIn && d.changes}
                              </div>
                            </div>
                            <div style={{ display:"flex", gap:6 }}>
                              <button onClick={()=>setViewingNotif(notif)} style={{
                                padding:"6px 12px", borderRadius:4, background:T.accent+"20",
                                border:`1px solid ${T.accent}40`, color:T.accent,
                                fontFamily:sans, fontSize:11, cursor:"pointer", fontWeight:600 }}>
                                Details
                              </button>
                              <button onClick={()=>dismissNotification(notif.id)} style={{
                                padding:"6px 10px", borderRadius:4, background:"transparent",
                                border:`1px solid ${T.border}`, color:T.dim,
                                fontFamily:sans, fontSize:11, cursor:"pointer" }}>
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Arrival summary */}
                <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
                  <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8,
                    padding:"14px 20px", flex:1, minWidth:120, borderTop:`3px solid ${T.accent}` }}>
                    <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:600, marginBottom:4 }}>TOTAL CADETS</div>
                    <div style={{ fontFamily:mono, color:T.accent, fontSize:26, fontWeight:700 }}>{coRoster.length}</div>
                  </div>
                  <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8,
                    padding:"14px 20px", flex:1, minWidth:120, borderTop:`3px solid ${T.blue}` }}>
                    <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:600, marginBottom:4 }}>ARRIVED</div>
                    <div style={{ fontFamily:mono, color:T.blue, fontSize:26, fontWeight:700 }}>{arrivedCount}</div>
                  </div>
                  <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8,
                    padding:"14px 20px", flex:1, minWidth:120, borderTop:`3px solid ${T.gold}` }}>
                    <div style={{ fontFamily:sans, color:T.dim, fontSize:11, fontWeight:600, marginBottom:4 }}>NOT YET ARRIVED</div>
                    <div style={{ fontFamily:mono, color:T.gold, fontSize:26, fontWeight:700 }}>{unarrivedCount}</div>
                  </div>
                </div>

                {/* Search */}
                <div style={{ marginBottom:14 }}>
                  <input value={rosterSearch}
                    onChange={e=>setRosterSearch(e.target.value)}
                    placeholder="Search by name, rank, or username..."
                    style={{ background:T.bg, border:`1px solid ${T.border}`, color:T.light,
                      fontFamily:sans, fontSize:13, padding:"9px 14px", borderRadius:6,
                      outline:"none", maxWidth:320, width:"100%", boxSizing:"border-box" }}
                    onFocus={e=>e.target.style.borderColor=T.accent}
                    onBlur={e=>e.target.style.borderColor=T.border} />
                </div>

                {coRoster.length === 0 ? (
                  <div style={{ border:`2px dashed ${T.border}`, borderRadius:8, padding:"48px 32px", textAlign:"center" }}>
                    <div style={{ fontSize:32, marginBottom:12 }}>👥</div>
                    <div style={{ fontFamily:sans, color:T.light, fontSize:16, fontWeight:700, marginBottom:8 }}>No Roster Loaded</div>
                    <div style={{ fontFamily:sans, color:T.dim, fontSize:13 }}>S1 staff have not yet uploaded a roster for {unlocked}.</div>
                  </div>
                ) : (
                  <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:"hidden", overflowX:"auto" }}>
                    <div style={{ display:"grid", gridTemplateColumns:"70px 120px 120px 100px 40px 50px 60px 60px 70px 100px 60px 160px 80px 80px",
                      minWidth:1240, padding:"10px 16px", background:T.card2,
                      fontFamily:sans, color:T.dim, fontSize:10, fontWeight:700,
                      letterSpacing:0.5, textTransform:"uppercase" }}>
                      <span>A#</span><span>Username</span><span>Last</span><span>First</span>
                      <span>MI</span><span>BN</span><span>Plt</span><span>Sqd</span>
                      <span>Rank</span><span>Duty</span><span>Year</span><span>Email</span><span>Room</span><span>Arrived</span>
                    </div>
                    {filteredCoRoster.length===0&&(
                      <div style={{ padding:24, textAlign:"center", fontFamily:sans, color:T.dim }}>No results</div>
                    )}
                    {filteredCoRoster.map((c,i)=>{
                      const arrived = (c.arrived||"").toUpperCase() === "Y";
                      return (
                        <div key={i} style={{ display:"grid",
                          gridTemplateColumns:"70px 120px 120px 100px 40px 50px 60px 60px 70px 100px 60px 160px 80px 80px",
                          minWidth:1240, padding:"10px 16px", borderTop:`1px solid ${T.border}`,
                          background:i%2===0?"transparent":T.card2, alignItems:"center" }}>
                          <span style={{ fontFamily:mono, color:T.faint, fontSize:11 }}>{c.a}</span>
                          <span style={{ fontFamily:mono, color:T.dim,   fontSize:11 }}>{c.username}</span>
                          <span style={{ fontFamily:sans, color:T.accent, fontSize:12, fontWeight:600, cursor:"pointer", textDecoration:"underline" }} onClick={()=>setSelectedBoardCadet(c)}>{c.last}</span>
                          <span style={{ fontFamily:sans, color:T.light, fontSize:12 }}>{c.first}</span>
                          <span style={{ fontFamily:sans, color:T.dim,   fontSize:11 }}>{c.mi}</span>
                          <span style={{ fontFamily:mono, color:T.dim,   fontSize:11 }}>{c.bn}</span>
                          <span style={{ fontFamily:mono, color:T.dim,   fontSize:11 }}>{c.plt}</span>
                          <span style={{ fontFamily:mono, color:T.dim,   fontSize:11 }}>{c.sqd}</span>
                          <span style={{ fontFamily:mono, color:T.tan,   fontSize:12, fontWeight:700 }}>{c.rank}</span>
                          <span style={{ fontFamily:sans, color:T.dim,   fontSize:11 }}>{c.duty}</span>
                          <span style={{ fontFamily:mono, color:T.dim,   fontSize:11 }}>{c.cadetYear}</span>
                          <span style={{ fontFamily:sans, color:T.faint, fontSize:11 }}>{c.email}</span>
                          <span style={{ fontFamily:mono, color:T.accent,fontSize:12 }}>{c.room}</span>
                          <span style={{
                            fontFamily:sans, fontSize:12, fontWeight:700,
                            color: arrived ? T.accent : T.gold,
                          }}>
                            {arrived ? "✓ YES" : "✗ NO"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── ACCOUNTABILITY MASTER LIST TAB ── */}
            {boardTab === "acctmaster" && (() => {
              // Pull ALL Morning Formation accountability reports for this company
              // regardless of approval status — this is a company tracking tool
              const allAcctSubs = [
                ...submissions.filter(s =>
                  s.unit===unlocked && s.type==="Accountability" &&
                  (s.details?.formationType||"").toLowerCase().includes("morning")
                ),
                ...clearedSubmissions.filter(s =>
                  s.unit===unlocked && s.type==="Accountability" &&
                  (s.details?.formationType||"").toLowerCase().includes("morning")
                ),
              ];

              // Unique sorted formation dates (for the on-screen table legend count)
              const formDates = [...new Set(
                allAcctSubs.map(s => s.details?.formationDate || s.date).filter(Boolean)
              )].sort();

              const downloadMasterList = () =>
                downloadCompanyAccountabilityMaster(unlocked, coRoster, allAcctSubs);

              return (
                <div style={{ textAlign:"center", padding:"48px 32px" }}>
                  <div style={{ fontSize:40, marginBottom:16 }}>📅</div>
                  <div style={{ fontFamily:sans, color:T.light, fontSize:20, fontWeight:700, marginBottom:8 }}>
                    Morning Formation Master List
                  </div>
                  <div style={{ fontFamily:sans, color:T.dim, fontSize:13, lineHeight:1.7,
                    maxWidth:520, margin:"0 auto 20px" }}>
                    A separate status-board spreadsheet for each day a report has been filed —
                    organized by HQ, platoon, and squad, matching the official accountability-tracker format.
                    <div style={{ marginTop:12, display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap" }}>
                      {[
                        ["#00B050","#ffffff","P — Present"],
                        ["#FFC000","#000000","EA — Excused Absent"],
                        ["#7030A0","#ffffff","UA — Unexcused Absent"],
                        ["#FFFF00","#000000","LV — Leave"],
                        ["#0070C0","#ffffff","SAB — Study Abroad"],
                      ].map(([bg,col,label])=>(
                        <span key={label} style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
                          <span style={{ width:16, height:16, borderRadius:3, background:bg, display:"inline-block", flexShrink:0 }}/>
                          <span style={{ fontFamily:sans, color:T.dim, fontSize:12 }}>{label}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {coRoster.length === 0 ? (
                    <div style={{ fontFamily:sans, color:T.red, fontSize:13, marginBottom:20 }}>
                      ⚠ No roster loaded for {unlocked}. S1 must upload a roster first.
                    </div>
                  ) : formDates.length === 0 ? (
                    <div style={{ fontFamily:sans, color:T.gold, fontSize:13, marginBottom:20 }}>
                      No Morning Formation reports have been submitted for {unlocked} yet.
                      Once accountability reports are submitted and approved, they will appear here.
                    </div>
                  ) : (
                    <div style={{ fontFamily:sans, color:T.dim, fontSize:13, marginBottom:20 }}>
                      {coRoster.length} cadets &nbsp;·&nbsp; {formDates.length} morning formation{formDates.length!==1?"s":""} on record
                    </div>
                  )}

                  <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
                    <button
                      onClick={() => setAcctCalendar(true)}
                      disabled={formDates.length===0}
                      style={{
                        padding:"13px 32px", borderRadius:8,
                        background: formDates.length>0 ? T.card2 : "transparent",
                        color: formDates.length>0 ? T.accent : T.faint,
                        fontFamily:sans, fontSize:15, fontWeight:700,
                        border:`2px solid ${formDates.length>0 ? T.accent : T.faint}`,
                        cursor: formDates.length>0 ? "pointer" : "not-allowed",
                        opacity: formDates.length>0 ? 1 : 0.4,
                      }}>
                      📅 View by Date
                    </button>
                    <button
                      onClick={downloadMasterList}
                      disabled={coRoster.length===0 || formDates.length===0}
                      style={{
                        padding:"13px 32px", borderRadius:8,
                        background: coRoster.length>0&&formDates.length>0 ? T.accent : T.faint,
                        color:"#0f1410", fontFamily:sans, fontSize:15, fontWeight:700,
                        border:"none",
                        cursor: coRoster.length>0&&formDates.length>0 ? "pointer" : "not-allowed",
                        opacity: coRoster.length>0&&formDates.length>0 ? 1 : 0.4,
                      }}>
                      ⬇ Download Excel
                    </button>
                  </div>
                </div>
              );
            })()}

          </>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────
//  TOPBAR
// ─────────────────────────────────────────────
function Topbar({ view, setView, s1Unlocked, onDashClick }) {
  return (
    <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`,
      padding:"0 36px", display:"flex", alignItems:"center",
      justifyContent:"space-between", height:62, position:"sticky", top:0, zIndex:100,
      boxShadow:"0 1px 20px #00000050" }}>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ width:38, height:38, borderRadius:8, background:T.olive,
          border:`1px solid ${T.accent}60`, display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:mono, color:T.accent, fontWeight:700, fontSize:14 }}>S1</div>
        <div>
          <div style={{ fontFamily:sans, color:T.light, fontSize:13, fontWeight:700 }}>1st Battalion S-1</div>
          <div style={{ fontFamily:sans, color:T.faint, fontSize:11 }}>Admin & Human Resources Portal</div>
        </div>
      </div>
      <nav style={{ display:"flex", gap:4 }}>
        {[
          { label:"Submit Request",  key:"SUBMIT REQUEST" },
          { label:"Status Board",    key:"STATUS BOARD" },
          { label:"S1 Dashboard",    key:"S1 DASHBOARD", lock:!s1Unlocked },
        ].map(({label,key,lock})=>{
          const active = view===key;
          return (
            <button key={key} onClick={()=>key==="S1 DASHBOARD"?onDashClick():setView(key)} style={{
              background:active?T.accent:"transparent", color:active?"#0f1410":T.dim,
              border:`1px solid ${active?T.accent:T.border}`,
              fontFamily:sans, fontSize:12, fontWeight:600, padding:"7px 18px", borderRadius:6,
              cursor:"pointer", display:"flex", alignItems:"center", gap:6, transition:"all 0.15s" }}>
              {lock && <span style={{ fontSize:11 }}>🔒</span>}
              {label}
            </button>
          );
        })}
      </nav>
      <div style={{ fontFamily:mono, color:T.faint, fontSize:10 }}>
        {new Date().toLocaleDateString("en-US",{weekday:"short",year:"numeric",month:"short",day:"numeric"}).toUpperCase()}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  APP ROOT
// ─────────────────────────────────────────────
//  SITE ACCESS GATE
// ─────────────────────────────────────────────
function SiteGate({ onUnlock }) {
  const [pw, setPw]         = useState("");
  const [err, setErr]       = useState(false);
  const [shake, setShake]   = useState(false);
  const [loading, setLoading] = useState(false);

  const attempt = async () => {
    if (!pw || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/site`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) { onUnlock(); return; }
    } catch(e) { console.error(e); }
    setLoading(false);
    setErr(true); setShake(true); setPw("");
    setTimeout(() => setShake(false), 500);
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center",
      justifyContent:"center", padding:24 }}>
      <style>{`
        * { box-sizing:border-box; }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-10px)} 60%{transform:translateX(10px)} }
        select option { background:#1c2219; color:#d8d4c8; }
        ::-webkit-scrollbar { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background:#0f1410; }
        ::-webkit-scrollbar-thumb { background:#2a3328; border-radius:3px; }
      `}</style>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12,
        padding:"52px 56px", maxWidth:420, width:"100%", textAlign:"center",
        borderTop:`3px solid ${T.accent}`, boxShadow:"0 20px 60px #00000070",
        animation: shake ? "shake 0.4s" : "none" }}>
        <div style={{ width:60, height:60, borderRadius:"50%", background:T.olive,
          border:`2px solid ${T.accent}`, display:"flex", alignItems:"center",
          justifyContent:"center", margin:"0 auto 22px",
          fontFamily:mono, color:T.accent, fontSize:18, fontWeight:700, letterSpacing:1 }}>1BN</div>
        <div style={{ fontFamily:sans, color:T.light, fontSize:21, fontWeight:700, marginBottom:6 }}>
          1st Battalion S-1 Portal
        </div>
        <div style={{ fontFamily:sans, color:T.dim, fontSize:13, marginBottom:36, lineHeight:1.6 }}>
          Restricted system — authorized personnel only.<br />Enter your access code to continue.
        </div>
        <div style={{ marginBottom: err ? 8 : 20 }}>
          <input
            type="password"
            value={pw}
            autoFocus
            onChange={e => { setPw(e.target.value); setErr(false); }}
            onKeyDown={e => e.key === "Enter" && attempt()}
            placeholder="Access code"
            style={{ width:"100%", padding:"13px 16px", background:T.surface,
              border:`1px solid ${err ? T.red : T.border}`, borderRadius:6, outline:"none",
              fontFamily:mono, fontSize:15, color:T.light, letterSpacing:3, textAlign:"center",
              transition:"border-color 0.15s" }}
          />
        </div>
        {err && (
          <div style={{ fontFamily:sans, color:T.red, fontSize:12, marginBottom:16 }}>
            ⚠ Invalid access code
          </div>
        )}
        <button onClick={attempt} disabled={!pw||loading} style={{
          width:"100%", padding:"13px", borderRadius:6, background:T.accent,
          color:"#0f1410", border:"none", fontFamily:sans, fontSize:14, fontWeight:700,
          cursor: (pw&&!loading) ? "pointer" : "not-allowed", opacity: (pw&&!loading) ? 1 : 0.5, letterSpacing:0.5 }}>
          {loading ? "Verifying…" : "Authenticate →"}
        </button>
        <div style={{ fontFamily:sans, color:T.faint, fontSize:11, marginTop:22 }}>
          Unauthorized access is prohibited · UNCLASSIFIED // FOUO
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  APP ROOT
// ─────────────────────────────────────────────
export default function App() {
  const [siteUnlocked, setSiteUnlocked]       = useState(() => sessionStorage.getItem("s1_auth") === "1");
  const [view, setView]                       = useState("SUBMIT REQUEST");
  const [submissions, setSubmissions]         = useState([]);
  const [clearedSubmissions, setClearedSubs]  = useState([]); // { ...sub, clearedAt: ISO string }
  const [roster, setRoster]                   = useState([]);
  const [s1Unlocked, setS1Unlocked]           = useState(false);
  const [showGate, setShowGate]               = useState(false);
  const [rosterNotifications, setRosterNotifications] = useState([]); // { id, company, type, cadet, details, timestamp }

  const handleSiteUnlock = () => { sessionStorage.setItem("s1_auth", "1"); setSiteUnlocked(true); };

  // Load all persisted data from the API on mount.
  // A 403 means the server session expired — clear local auth and show login again.
  useEffect(() => {
    if (!siteUnlocked) return;
    const load = async () => {
      try {
        const responses = await Promise.all([
          fetch(`${API}/roster`),
          fetch(`${API}/submissions`),
          fetch(`${API}/cleared-submissions`),
          fetch(`${API}/roster-notifications`),
        ]);
        if (responses.some(r => r.status === 403)) {
          sessionStorage.removeItem('s1_auth');
          setSiteUnlocked(false);
          return;
        }
        const [r, s, c, n] = await Promise.all(responses.map(r => r.json()));
        setRoster(r);
        setSubmissions(s);
        setClearedSubs(c);
        setRosterNotifications(n);
      } catch(e) { console.error('Initial data load:', e); }
    };
    load();
  }, [siteUnlocked]);

  if (!siteUnlocked) return <SiteGate onUnlock={handleSiteUnlock} />;

  const handleDashClick = () => { setView("S1 DASHBOARD"); if (!s1Unlocked) setShowGate(true); };
  const handleUnlock    = () => { setS1Unlocked(true); setShowGate(false); };
  const handleLock      = () => { setS1Unlocked(false); setView("SUBMIT REQUEST"); };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, color:T.light }}>
      <style>{`
        * { box-sizing:border-box; }
        input[type=date]::-webkit-calendar-picker-indicator,
        input[type=datetime-local]::-webkit-calendar-picker-indicator { filter:invert(0.6); }
        select option { background:#1c2219; color:#d8d4c8; }
        ::-webkit-scrollbar { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background:#0f1410; }
        ::-webkit-scrollbar-thumb { background:#2a3328; border-radius:3px; }
      `}</style>
      <Topbar view={view} setView={setView} s1Unlocked={s1Unlocked} onDashClick={handleDashClick} />
      <div style={{ minHeight:"calc(100vh - 100px)" }}>
        {view==="SUBMIT REQUEST" && <SubmitForm submissions={submissions} setSubmissions={setSubmissions} roster={roster} />}
        {view==="STATUS BOARD"   && <CompanyStatusBoard submissions={submissions} clearedSubmissions={clearedSubmissions} roster={roster} rosterNotifications={rosterNotifications} setRosterNotifications={setRosterNotifications} />}
        {view==="S1 DASHBOARD"   && (
          !s1Unlocked || showGate
            ? <S1Gate onUnlock={handleUnlock} />
            : <Dashboard
                submissions={submissions} setSubmissions={setSubmissions}
                clearedSubmissions={clearedSubmissions} setClearedSubs={setClearedSubs}
                roster={roster} setRoster={setRoster} onLock={handleLock}
                rosterNotifications={rosterNotifications} setRosterNotifications={setRosterNotifications} />
        )}
      </div>
      <div style={{ borderTop:`1px solid ${T.border}`, padding:"12px 36px",
        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontFamily:sans, color:T.faint, fontSize:11 }}>1st Battalion S-1 · For Official Use Only</div>
        <div style={{ fontFamily:mono, color:T.faint, fontSize:10 }}>UNCLASSIFIED // FOUO</div>
      </div>
    </div>
  );
}
